<template>
  <div class="coach-cms">
    <div class="shell">
      <!-- Top Bar -->
      <div class="topbar">
        <!-- <div class="logo">Dia<span>B</span> AI Coach</div> -->
        <div class="tbadge" :class="stats.totalPending > 0 ? 'tb-warn' : 'tb-ok'">
          {{ stats.totalPending > 0 ? `${stats.totalPending} chờ duyệt` : 'Đã xử lý hết' }}
        </div>
        <div class="tbar-right d-none d-md-flex">
          <!-- Mở được không cần chọn tin: tin đã duyệt mà chỉ số chưa lưu không còn trong danh sách Chờ duyệt -->
          <span>
            <b-button class="btn btn-outline-primary ml-2" @click="openKpiOverview">
              <span class="svg-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <line x1="4" y1="20" x2="4" y2="10"></line>
                  <line x1="10" y1="20" x2="10" y2="4"></line>
                  <line x1="16" y1="20" x2="16" y2="13"></line>
                  <line x1="22" y1="20" x2="2" y2="20"></line>
                </svg>
              </span>
              Cập nhật chỉ số
            </b-button>
          </span>
          <!-- Khung giờ coach nhận nhắc tin chờ duyệt từ cron -->
          <b-button class="btn btn-outline-primary ml-2" @click="showActiveHours = true">
            <span class="svg-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <circle cx="12" cy="12" r="9"></circle>
                <polyline points="12 7 12 12 15 14"></polyline>
              </svg>
            </span>
            Thời gian hoạt động
          </b-button>
          <b-button v-if="canManageAll" class="btn btn-outline-primary ml-2" @click.stop="openGroupConfig">
            <span class="svg-icon">
              <inline-svg src="/media/svg/icons/Neolex/Basic/settings.svg" />
            </span>
            Cấu hình nhóm
          </b-button>
          <b-button class="btn btn-success ml-2" @click="handleRefresh">
            <span class="svg-icon">
              <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" />
            </span>
            Làm mới
          </b-button>
        </div>
      </div>

      <coach-active-hours-modal v-model="showActiveHours" :can-manage-all="canManageAll" :current-user="currentUser" />

      <!-- Group Config Modal -->
      <b-modal
        v-model="showGroupConfigModal"
        title="Cấu hình nhóm AI Coach"
        size="lg"
        centered
        @ok="saveGroupConfig"
        ok-title="Lưu"
        cancel-title="Hủy"
      >
        <div class="group-config-content">
          <p class="group-config-desc">
            Chọn nhóm để bắt đầu<strong> chat AI</strong>. Mọi tin AI đều chờ coach duyệt trước khi gửi.
          </p>

          <div class="group-search-box">
            <b-form-input
              type="text"
              v-model="groupSearchQuery"
              placeholder="Tìm nhóm..."
              class="group-search-input"
            />
            <!-- Mở modal chọn văn phong few-shot cho các nhóm đang bật AI. Tạm ẩn trên mobile, chỉ cấu hình ở desktop -->
            <button
              v-if="showStyleCoachPicker"
              type="button"
              class="group-style-btn d-none d-md-inline-flex"
              :disabled="!selectedAIGroupIds.length"
              :title="selectedAIGroupIds.length ? '' : 'Bật AI cho ít nhất 1 nhóm trước'"
              @click="openStyleModal(null)"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                <line x1="8" y1="9" x2="16" y2="9"></line>
                <line x1="8" y1="13" x2="13" y2="13"></line>
              </svg>
              Văn phong
            </button>
          </div>

          <!-- Nhóm có trên OA nhưng chưa gắn customer_receives nào: mặc định ẩn, bỏ tick để hiện.
               Nhóm đã hoàn thành thực tế đã giải tán, không còn trên OA, chỉ còn trong cấu hình bật AI -->
          <b-form-checkbox v-model="hideUnassignedGroups" class="mb-2" size="sm">
            Ẩn nhóm chưa được chỉ định<template v-if="unassignedGroupCount"> ({{ unassignedGroupCount }})</template>
          </b-form-checkbox>

          <div class="group-list">
            <div
              v-for="group in filteredAllGroups"
              :key="group.group_id"
              class="group-item"
              :class="{ selected: isAIGroupSelected(group) }"
            >
              <b-form-checkbox
                :checked="isAIGroupSelected(group)"
                class="group-checkbox-bs"
                @change="toggleAIGroupSelection(group)"
              />
              <div class="group-info" @click="toggleAIGroupSelection(group)">
                <div class="group-name">{{ group.name }}</div>
                <div class="group-id">ID: {{ group.group_id }}</div>
              </div>
              <button
                v-if="isAIGroupSelected(group) && (aiCandidateCounts[group.group_id] || 0) >= 2"
                type="button"
                class="group-target-btn"
                title="Chọn khách hàng AI theo dõi trong nhóm"
                @click.stop="openAiTargetPicker(group, false)"
              >
                👤 {{ aiTargetLabel(group.group_id) }}
              </button>
            </div>
            <div v-if="filteredAllGroups.length === 0 && !loadingGroups" class="no-groups">
              Không tìm thấy nhóm
            </div>
          </div>

          <div class="selected-count">
            Bật AI: <strong>{{ selectedAIGroupIds.length }}</strong> nhóm
          </div>
        </div>
      </b-modal>

      <!-- Nhóm có từ 2 người (không tính coach/OA): chọn 1 người cho AI theo dõi -->
      <b-modal
        v-model="aiTarget.show"
        title="Chọn khách hàng AI theo dõi"
        size="md"
        centered
        no-close-on-backdrop
        ok-title="Chọn"
        cancel-title="Huỷ"
        ok-variant="success"
        @ok="confirmAiTarget"
        @cancel="cancelAiTarget"
        @close="cancelAiTarget"
      >
        <div class="ai-target-hint">
          <div class="ai-target-hint__group">{{ aiTarget.group && aiTarget.group.name }}</div>
          <div class="ai-target-hint__note">AI chỉ trả lời tin của khách hàng được chọn.</div>
        </div>
        <div v-if="aiTarget.loading" class="ai-target-empty">Đang tải…</div>
        <template v-else>
          <div class="ai-target-item" :class="{ active: aiTarget.choice === '' }" @click="aiTarget.choice = ''">
            <b-form-radio v-model="aiTarget.choice" value="" name="ai-target" @click.native.stop />
            <div>
              <div class="ai-target-name">Theo dõi tất cả</div>
              <div class="ai-target-meta">AI xử lý tin của mọi người trong nhóm</div>
            </div>
          </div>
          <div
            v-for="c in aiTarget.candidates"
            :key="c.zaloUserId"
            class="ai-target-item"
            :class="{ active: aiTarget.choice === c.zaloUserId }"
            @click="aiTarget.choice = c.zaloUserId"
          >
            <b-form-radio v-model="aiTarget.choice" :value="c.zaloUserId" name="ai-target" @click.native.stop />
            <img v-if="c.avatar" :src="c.avatar" class="ai-target-avatar" alt="" />
            <div class="ai-target-text">
              <div class="ai-target-name">{{ c.name || c.zaloUserId }}</div>
            </div>
          </div>
        </template>
      </b-modal>

      <!-- Văn phong few-shot theo nhóm × chủ đề. Trái: nhóm đang bật AI; phải: chọn coach mẫu cho từng chủ đề -->
      <b-modal
        id="style-coach-modal"
        v-model="showStyleModal"
        title="Văn phong cho nhóm chat"
        size="xl"
        centered
        scrollable
        dialog-class="kov-dialog"
        @hide="onStyleModalHide"
      >
        <template #modal-footer>
          <span v-if="styleChanges.length" class="style-footer__hint">{{ styleChanges.length }}<span class="d-none d-sm-inline"> thay đổi</span> chưa lưu</span>
          <button type="button" class="btn btn-light btn-sm" :disabled="savingStyles" @click="$bvModal.hide('style-coach-modal')">Đóng</button>
          <button type="button" class="btn btn-success btn-sm" :disabled="savingStyles || !styleChanges.length" @click="saveStyleChanges">
            {{ savingStyles ? 'Đang lưu...' : 'Lưu' }}
          </button>
        </template>
        <div class="kov">
          <aside class="kov-nav">
            <div class="kov-nav__head">
              <b-form-input v-model.trim="styleGroupSearch" class="kov-select kov-nav__search" placeholder="Tìm nhóm..." />
            </div>
            <div v-if="styleGroupsFiltered.length === 0" class="kov-nav__empty">Không có nhóm đang bật AI</div>
            <div v-else class="kov-nav__list">
              <button
                v-for="g in styleGroupsFiltered"
                :key="g.group_id"
                type="button"
                class="kov-nav__item"
                :class="{ active: styleGroupId === g.group_id }"
                @click="styleGroupId = g.group_id"
              >
                <span class="kov-nav__name">{{ g.name || g.group_id }}</span>
                <span v-if="customStyleCount(g.group_id)" class="kov-nav__pending" title="Số chủ đề đã chọn coach mẫu">{{ customStyleCount(g.group_id) }}</span>
              </button>
            </div>
          </aside>

          <section class="kov-main">
            <div v-if="!styleGroupId" class="kov-empty">Chọn một nhóm bên trái</div>
            <template v-else>
              <div class="kov-main__head">
                <div class="kov-main__name">{{ styleGroupName }}</div>
              </div>
              <p class="style-hint">
                Chọn coach để AI học văn phong của coach đó ở từng chủ đề (tối đa 5 ví dụ mỗi lượt).
                <strong>Không dùng mẫu</strong>: AI tự viết, coach duyệt dần để có dữ liệu.
                <template v-if="styleMinSamples > 1">Chỉ hiện coach có từ {{ styleMinSamples }} mẫu đã duyệt trở lên.</template>
              </p>

              <div v-for="topic in styleTopics" :key="topic.key" class="style-topic">
                <div class="style-topic__title">{{ topic.label }}</div>
                <div class="style-options">
                  <button
                    type="button"
                    class="style-option"
                    :class="{ active: !topicStyleOf(styleGroupId, topic.key).coachId }"
                    :disabled="savingStyles"
                    @click="pickTopicStyle(topic.key, '')"
                  >
                    <span class="style-option__name">Không dùng mẫu</span>
                    <span class="style-option__meta">AI tự viết, không gửi ví dụ</span>
                  </button>
                  <button
                    v-for="coach in topic.coaches"
                    :key="coach.coachId"
                    type="button"
                    class="style-option"
                    :class="{ active: topicStyleOf(styleGroupId, topic.key).coachId === coach.coachId }"
                    :disabled="savingStyles"
                    @click="pickTopicStyle(topic.key, coach.coachId)"
                  >
                    <span class="style-option__name">{{ coach.name }}</span>
                    <span class="style-option__meta">{{ coach.samples }} mẫu · {{ coach.edited }} coach tự sửa</span>
                  </button>
                  <span v-if="!topic.coaches.length" class="style-empty">Chưa có dữ liệu</span>
                </div>

                <!-- Câu mẫu của coach đã chọn: ghim tối đa 5, không ghim = 5 câu mới nhất -->
                <div v-if="topicStyleOf(styleGroupId, topic.key).coachId" class="style-samples">
                  <button type="button" class="style-samples__toggle" @click="toggleStyleSamples(topic.key)">
                    <span>
                      Câu mẫu:
                      <strong v-if="topicStyleOf(styleGroupId, topic.key).pairIds.length">
                        đã chọn {{ topicStyleOf(styleGroupId, topic.key).pairIds.length }}/{{ styleMaxPairs }}
                      </strong>
                      <strong v-else>{{ styleMaxPairs }} câu mới nhất (mặc định)</strong>
                    </span>
                    <span class="style-samples__caret">{{ expandedSampleTopic === topic.key ? 'Thu gọn' : 'Chọn câu' }}</span>
                  </button>

                  <div v-if="expandedSampleTopic === topic.key" class="style-samples__body">
                    <div v-if="samplesOf(topic.key).loading" class="style-empty">Đang tải...</div>
                    <div v-else-if="!samplesOf(topic.key).items.length" class="style-empty">Chưa có câu mẫu</div>
                    <template v-else>
                      <div class="style-samples__bar">
                        <span>Không chọn câu nào = dùng {{ styleMaxPairs }} câu mới nhất</span>
                        <button
                          v-if="topicStyleOf(styleGroupId, topic.key).pairIds.length"
                          type="button"
                          class="style-samples__clear"
                          :disabled="savingStyles"
                          @click="setTopicPairs(topic.key, [])"
                        >Bỏ chọn hết</button>
                      </div>
                      <div
                        v-for="s in samplesOf(topic.key).items"
                        :key="s.id"
                        class="style-sample"
                        :class="{
                          active: isPairPinned(topic.key, s.id),
                          disabled: !isPairPinned(topic.key, s.id) && topicStyleOf(styleGroupId, topic.key).pairIds.length >= styleMaxPairs
                        }"
                        @click="!savingStyles && (isPairPinned(topic.key, s.id) || topicStyleOf(styleGroupId, topic.key).pairIds.length < styleMaxPairs) && togglePinnedPair(topic.key, s.id)"
                      >
                        <b-form-checkbox
                          :checked="isPairPinned(topic.key, s.id)"
                          :disabled="savingStyles
                            || (!isPairPinned(topic.key, s.id) && topicStyleOf(styleGroupId, topic.key).pairIds.length >= styleMaxPairs)"
                          @change="togglePinnedPair(topic.key, s.id)"
                          @click.native.stop
                        />
                        <div class="style-sample__text">
                          <div class="style-sample__q"><b>KH:</b> {{ s.question }}</div>
                          <div class="style-sample__a"><b>Coach:</b> {{ s.answer }}</div>
                          <div class="style-sample__meta">
                            {{ s.sentAt ? formatDateTime(s.sentAt) : '' }}<template v-if="s.isEdited"> · coach tự sửa</template>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </template>
          </section>
        </div>
      </b-modal>

      <!-- Share Modal -->
      <b-modal
        id="share-item-modal"
        v-model="showShareModal"
        title="Nhờ tham vấn từ chuyên gia"
        size="md"
        @ok="handleShare"
        @show="loadCoachesForShare"
        @shown="makeDraggable('share-item-modal')"
        ok-title="Chia sẻ"
        cancel-title="Hủy"
        :ok-disabled="sharing || selectedShareCoachIds.length === 0"
      >
        <div class="share-modal-content">
          <div v-if="loadingCoaches" class="share-loading">Đang tải danh sách chuyên gia...</div>
          <div v-else class="share-coach-list">
            <div
              v-for="coach in availableCoaches"
              :key="coach.id"
              class="share-coach-item"
              :class="{ selected: isCoachSelected(coach.id) }"
              @click="toggleCoachSelection(coach.id)"
            >
              <div class="share-coach-checkbox">
                <span v-if="isCoachSelected(coach.id)" class="check-icon">✓</span>
              </div>
              <div class="share-coach-info">
                <div class="share-coach-name">{{ coach.fullName }}</div>
              </div>
            </div>
            <div v-if="availableCoaches.length === 0 && !loadingCoaches" class="no-coaches">Không có chuyên gia nào</div>
          </div>
          <div class="selected-count">Đã chọn: <strong>{{ selectedShareCoachIds.length }}</strong></div>

          <div class="share-note-wrap">
            <label class="share-note-label">Nội dung hỏi</label>
            <b-form-textarea class="share-note-textarea" v-model="contentAsk" placeholder="Nhập nội dung câu hỏi cần tư vấn..." rows="3"></b-form-textarea>
          </div>
        </div>
      </b-modal>

      <!-- Cập nhật chỉ số: cột trái là khách hàng còn chỉ số (tính cả tin đã duyệt mà chỉ số chưa lưu -
           những tin này đã rời danh sách Chờ duyệt), bên phải là chỉ số của người đang chọn theo chủ đề -->
      <b-modal
        id="kpi-overview-modal"
        v-model="showKpiOverview"
        title="Cập nhật chỉ số"
        size="xl"
        centered
        scrollable
        hide-footer
        dialog-class="kov-dialog"
      >
        <div class="kov">
          <aside class="kov-nav">
            <div class="kov-nav__head">
              <b-form-input v-model.trim="overviewPatientSearch" class="kov-select kov-nav__search" placeholder="Tìm khách hàng..." />
            </div>

            <div v-if="overviewPatientsLoading" class="kov-nav__empty"><span class="chp-spinner"></span></div>
            <div v-else-if="overviewPatientsFiltered.length === 0" class="kov-nav__empty">Không có khách hàng nào</div>
            <div v-else class="kov-nav__list">
              <button
                v-for="pt in overviewPatientsFiltered"
                :key="patientKey(pt)"
                type="button"
                class="kov-nav__item"
                :class="{ active: overviewPatientKey === patientKey(pt) }"
                @click="selectOverviewPatient(pt)"
              >
                <span class="kov-nav__avatar">{{ getInitials(pt.patientName || '?') }}</span>
                <span class="kov-nav__name">{{ pt.patientName || 'Chưa có tên' }}</span>
                <span v-if="pt.pendingCount" class="kov-nav__pending" :title="`${pt.pendingCount} chỉ số chờ lưu`">{{ pt.pendingCount }}</span>
                <span v-else class="kov-nav__count">{{ pt.totalCount }}</span>
              </button>
            </div>
          </aside>

          <section class="kov-main">
            <div v-if="!overviewAnchorId" class="kov-empty">Chọn một khách hàng bên trái</div>
            <template v-else>
            <div class="kov-main__head">
              <div class="kov-main__name">{{ overviewPatientName }}</div>
              <div class="kov-topics">
                <button
                  v-for="tab in overviewTabs"
                  :key="tab.key"
                  type="button"
                  class="kov-topic"
                  :class="{ active: overviewTab === tab.key }"
                  @click="overviewTab = tab.key; overviewExpandedId = null"
                >
                  {{ tab.label }}
                  <span class="kov-topic__count" :class="{ 'has-items': overviewCountByTab(tab.key) > 0 }">{{ overviewCountByTab(tab.key) }}</span>
                </button>
              </div>
            </div>

            <div class="kov-toolbar">
              <!-- Chip trạng thái kiêm bộ lọc: thấy số lượng và lọc trong 1 cú bấm -->
              <div class="kov-chips">
                <button
                  v-for="s in [
                    { value: 'all', label: 'Tất cả', count: overviewCountByTab(overviewTab), cls: '' },
                    { value: 0, label: 'Chờ lưu', count: overviewCountByStatus(0), cls: 'pending' },
                    { value: 1, label: 'Đã lưu', count: overviewCountByStatus(1), cls: 'saved' },
                    { value: 2, label: 'Bỏ qua', count: overviewCountByStatus(2), cls: 'skipped' },
                  ]"
                  :key="String(s.value)"
                  type="button"
                  class="kov-chip"
                  :class="[s.cls && `kov-chip--${s.cls}`, { active: overviewStatus === s.value }]"
                  @click="overviewStatus = s.value"
                >
                  {{ s.label }} <b>{{ s.count }}</b>
                </button>
              </div>
              <div class="kov-filters">
                <!-- Lọc cột khách hàng: chỉ người còn chỉ số chưa lưu -->
                <b-form-checkbox v-model="overviewPendingOnly" class="kov-filters__toggle" @change="$nextTick(loadKpiPatients)">
                  Chưa lưu
                </b-form-checkbox>
                <b-dropdown size="sm" variant="outline-secondary" right :text="`${overviewDays} ngày`" class="kov-dropdown">
                  <b-dropdown-item
                    v-for="n in [7, 30, 90]"
                    :key="n"
                    :active="overviewDays === n"
                    @click="overviewDays = n; onOverviewDaysChange()"
                  >
                    {{ n }} ngày
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>

            <div v-if="overviewLoading" class="kov-empty"><span class="chp-spinner"></span> Đang tải...</div>
            <div v-else-if="overviewFiltered.length === 0" class="kov-empty">Không có chỉ số nào</div>

            <table v-else class="kov-table">
              <thead>
                <tr v-if="overviewTab === 'blood_sugar'">
                  <th>Nhận lúc</th>
                  <th>Chỉ số</th>
                  <th>Tin gốc</th>
                  <th>Trạng thái</th>
                  <th class="text-right">Thao tác</th>
                </tr>
                <tr v-else>
                  <th>Nhận lúc</th>
                  <th>Bữa</th>
                  <th>Món</th>
                  <th>Calo</th>
                  <th>Điểm</th>
                  <th>Trạng thái</th>
                  <th class="text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="v in overviewFiltered">
                  <tr
                    :key="v.id"
                    class="kov-table__row"
                    :class="{ current: selectedItem && selectedItem.id === v.id, expanded: overviewExpandedId === v.id }"
                  >
                    <td class="kov-table__time" data-label="Nhận lúc">
                      <div class="kov-time__hm">{{ formatDateTime(v.createDatetime).split(' ')[0] }}</div>
                      <div class="kov-time__date">{{ formatDateTime(v.createDatetime).split(' ').slice(1).join(' ') }}</div>
                    </td>

                    <template v-if="overviewTab === 'blood_sugar'">
                      <td data-label="Chỉ số">
                        <div v-for="(r, i) in v.glucoseRows" :key="i" class="kov-measure">
                          <span class="kov-measure__tf">{{ glucoseTfLabel(r.timeFrameId) }}</span>
                          <strong>{{ r.value }}</strong> {{ r.unitType === 1 ? 'mg/dL' : 'mmol/L' }}
                          <span class="kov-measure__at">{{ r.createDate || 'lúc lưu' }}</span>
                        </div>
                      </td>
                      <td class="kov-table__msg" data-label="Tin gốc">{{ truncate(v.originalMessage || '', 70) }}</td>
                    </template>
                    <template v-else>
                      <td data-label="Bữa">{{ mealTfLabel(v.nutrition.timeFrameId) }}</td>
                      <td class="kov-table__msg" data-label="Món">{{ mealItemNames(v) }}</td>
                      <td data-label="Calo">{{ mealTotalCalories(v) }}</td>
                      <td data-label="Điểm">{{ v.nutrition.totalMealScore != null ? v.nutrition.totalMealScore : '' }}</td>
                    </template>

                    <td data-label="Trạng thái">
                      <span v-if="v.kpiStatus === 0" class="kov-badge kov-badge--pending">Chờ lưu</span>
                      <span v-else-if="v.kpiStatus === 1" class="kov-badge kov-badge--saved">Đã lưu</span>
                      <span v-else class="kov-badge kov-badge--skipped">Bỏ qua</span>
                    </td>
                    <td class="kov-table__actions">
                      <div class="kov-actions">
                        <button
                          v-if="v.kpiStatus === 0"
                          type="button"
                          class="kov-icon-btn kov-icon-btn--save"
                          :disabled="overviewBusyId === v.id"
                          @click="quickSaveKpi(v)"
                          v-b-tooltip.hover
                          title="Lưu"
                        >
                          <span v-if="overviewBusyId === v.id" class="btn-spinner"></span>
                          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                          <span class="kov-icon-btn__text">Lưu</span>
                        </button>
                        <button
                          type="button"
                          class="kov-icon-btn"
                          :class="{ active: overviewExpandedId === v.id && overviewExpandMode === 'view' }"
                          @click="toggleOverviewRow(v, 'view')"
                          v-b-tooltip.hover
                          title="Xem chi tiết"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        </button>
                        <button
                          v-if="v.kpiStatus === 0"
                          type="button"
                          class="kov-icon-btn"
                          :class="{ active: overviewExpandedId === v.id && overviewExpandMode === 'edit' }"
                          @click="toggleOverviewRow(v, 'edit')"
                          v-b-tooltip.hover
                          title="Sửa"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path></svg>
                        </button>
                        <!-- Bỏ qua thay cho xoá: chỉ số vẫn hiện để tra, chỉ không còn thao tác -->
                        <button
                          v-if="v.kpiStatus === 0"
                          type="button"
                          class="kov-icon-btn kov-icon-btn--danger"
                          :disabled="overviewBusyId === v.id"
                          @click="skipFromOverview(v)"
                          v-b-tooltip.hover
                          title="Bỏ qua"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><line x1="5.6" y1="5.6" x2="18.4" y2="18.4"></line></svg>
                        </button>
                      </div>
                    </td>
                  </tr>

                  <tr v-if="overviewExpandedId === v.id" :key="`${v.id}-detail`" class="kov-table__detail">
                    <td :colspan="overviewTab === 'blood_sugar' ? 5 : 7">
                      <!-- Cây hội thoại của session (1 session = 1 lần phân tích) sinh ra chỉ số này -->
                      <div v-if="v.sessionId" class="kov-thread">
                        <span class="kov-detail__label">Phiên phân tích</span>
                        <div v-if="threadOf(v).loading" class="kov-thread__empty">Đang tải…</div>
                        <div v-else-if="!threadOf(v).items.length" class="kov-thread__empty">Không có tin nào trong phiên</div>
                        <div
                          v-for="t in threadOf(v).items"
                          v-else
                          :key="t.id"
                          class="kov-thread__node"
                          :class="{ current: t.id === v.id }"
                        >
                          <div class="kov-thread__meta">
                            {{ formatDateTime(t.createDatetime) }}
                            <span class="kov-thread__status" :style="{ color: getStatusColor(t.status) }">· {{ t.statusName }}</span>
                            <span v-if="t.kpiType === 'non_target_chat'" class="kov-thread__status">· Tin từ khách hàng không chỉ định</span>
                            <span v-if="t.id === v.id" class="kov-thread__tag">Chỉ số này</span>
                          </div>
                          <div class="kov-thread__patient">
                            <div v-if="decisionImages(t.imageUrl).length" class="patient-images">
                              <img
                                v-for="url in decisionImages(t.imageUrl)"
                                :key="url"
                                :src="url"
                                class="patient-image"
                                alt="Ảnh khách hàng gửi"
                                @click="openMediaPreview(url, 'image')"
                                @error="markBadImage(url)"
                              />
                            </div>
                            <div v-if="t.originalMessage"><b>KH:</b> {{ t.originalMessage }}</div>
                          </div>
                          <div v-if="t.editedMessage || t.aiResponse" class="kov-thread__reply">
                            <b>{{ t.editedMessage ? 'Coach' : 'AI' }}:</b> {{ t.editedMessage || t.aiResponse }}
                          </div>
                        </div>
                      </div>
                      <div v-else-if="v.originalMessage || decisionImages(v.imageUrl).length" class="kov-detail__msg">
                        <span class="kov-detail__label">Khách hàng nhắn</span>
                        <div v-if="decisionImages(v.imageUrl).length" class="patient-images">
                          <img
                            v-for="url in decisionImages(v.imageUrl)"
                            :key="url"
                            :src="url"
                            class="patient-image"
                            alt="Ảnh khách hàng gửi"
                            @click="openMediaPreview(url, 'image')"
                            @error="markBadImage(url)"
                          />
                        </div>
                        <div v-if="v.originalMessage">{{ v.originalMessage }}</div>
                      </div>
                      <kpi-editor
                        :key="`${v.id}-${overviewExpandMode}`"
                        :view="v"
                        :start-editing="overviewExpandMode === 'edit'"
                        :readonly="overviewExpandMode === 'view'"
                        draft-only
                        @handled="onKpiHandled"
                        @updated="onKpiUpdated"
                        @draft="onKpiDraft"
                      />
                      <!-- Mở tin trong panel duyệt để xem đầy đủ hội thoại / duyệt câu trả lời -->
                      <button type="button" class="btn btn-sm kov-detail__open" title="Mở tin này trong panel duyệt" @click="openFromOverview(v)">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        Xem chi tiết
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            </template>
          </section>
        </div>
      </b-modal>

      <!-- Sidebar -->
      <!-- Mobile icon toolbar (filter + search) -->
      <div class="mobile-toolbar d-md-none">
        <button class="mtb-btn" :class="{ active: mobileFilterOpen }" @click="mobileFilterOpen = !mobileFilterOpen; mobileSearchOpen = false">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
          Lọc
          <span v-if="filter.status !== 0 || filter.kpiType !== null" class="mtb-dot"></span>
        </button>
        <button class="mtb-btn" :class="{ active: mobileSearchOpen }" @click="mobileSearchOpen = !mobileSearchOpen; mobileFilterOpen = false">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          Tìm kiếm
        </button>
        <div class="mtb-spacer"></div>
        <button class="mtb-btn" @click="openKpiOverview">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="4" y1="20" x2="4" y2="10"></line>
            <line x1="10" y1="20" x2="10" y2="4"></line>
            <line x1="16" y1="20" x2="16" y2="13"></line>
            <line x1="22" y1="20" x2="2" y2="20"></line>
          </svg>
          Chỉ số
        </button>
        <button v-if="canManageAll" class="mtb-btn" @click.stop="openGroupConfig">
          <inline-svg src="/media/svg/icons/Neolex/Basic/settings.svg" style="width:14px;height:14px;" />
          Cấu hình
        </button>
        <button class="mtb-btn" @click="handleRefresh">
          <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" style="width:14px;height:14px;" />
          Làm mới
        </button>
      </div>

      <div class="sidebar d-none d-md-flex">
        <!-- Bộ lọc coach: chỉ hiện cho người có quyền quản lý -->
        <div v-if="canManageAll" class="sec-lbl">HEALTH COACH</div>
        <div v-if="canManageAll" class="coach-filter-wrapper">
          <basic-select
            label=""
            placeholder="Tất cả Coach"
            name="coachFilter"
            :options="coachOptions"
            track-by="value"
            value-label="text"
            :value.sync="selectedCoachOption"
            @input="onCoachSelect"
          />
        </div>

        <div class="sec-lbl">TRẠNG THÁI</div>
        <div
          class="nav"
          :class="{ on: filter.status === null && filter.isShared !== true }"
          @click="setStatusFilter(null)"
        >
          <span class="ndot" style="background:#888;"></span>
          Tất cả
        </div>
        <div
          class="nav"
          :class="{ on: filter.status === 0 }"
          @click="setStatusFilter(0)"
        >
          <span class="ndot" style="background:#BA7517;"></span>
          Chờ duyệt
          <span class="nc" v-if="stats.totalPending">{{ stats.totalPending }}</span>
        </div>
        <div
          class="nav"
          :class="{ on: filter.status === 1 }"
          @click="setStatusFilter(1)"
        >
          <span class="ndot" style="background:#378ADD;"></span>
          Đã chỉnh sửa
          <span class="nc nc-gray" v-if="stats.totalEdited">{{ stats.totalEdited }}</span>
        </div>
        <div
          class="nav"
          :class="{ on: filter.status === 3 }"
          @click="setStatusFilter(3)"
        >
          <span class="ndot" style="background:#1D9E75;"></span>
          Đã gửi
          <span class="nc" v-if="stats.totalSent">{{ stats.totalSent }}</span>
        </div>
        <div
          class="nav"
          :class="{ on: filter.status === 4 }"
          @click="setStatusFilter(4)"
        >
          <span class="ndot" style="background:#6B7280;"></span>
          Bỏ qua
          <span class="nc nc-gray" v-if="stats.totalSkipped">{{ stats.totalSkipped }}</span>
        </div>
        <div
          class="nav"
          :class="{ on: filter.isShared === true }"
          @click="setSharedFilter(true)"
        >
          <span class="ndot" style="background:#9B59B6;"></span>
          Chia sẻ
          <span class="nc nc-purple" v-if="stats.totalShared">{{ stats.totalShared }}</span>
        </div>

        <div class="sec-lbl">Chủ đề</div>
        <div
          class="nav"
          :class="{ on: filter.kpiType === null }"
          @click="setKpiFilter(null)"
        >
          <span class="ndot" style="background:#888;"></span>
          Tất cả
        </div>
        <div
          v-for="kpi in kpiFilterOptions"
          :key="kpi.value"
          class="nav"
          :class="{ on: filter.kpiType === kpi.value }"
          @click="setKpiFilter(kpi.value)"
        >
          <span class="ndot" :style="{ background: kpi.dot }"></span>
          {{ kpi.label }}
        </div>
      </div>

      <!-- Patient List (Accordion) -->
      <div class="kh-list">
        <div class="kh-header">
          <div class="kh-title">Danh sách nhóm</div>
          <div class="search-mini d-none d-md-flex">
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
              <circle cx="6.5" cy="6.5" r="5"></circle>
              <path d="M10.5 10.5l3.5 3.5"></path>
            </svg>
            <b-form-input
              type="text"
              v-model="searchQuery"
              placeholder="Tìm nhóm"
              @input="onSearchDebounce"
            />
          </div>
        </div>
        <div class="kh-items" :class="{ 'loading-state': loading }">
          <!-- Group Level -->
          <div
            v-for="group in groupedData"
            :key="group.groupKey"
            class="acc-group"
          >
            <!-- Group Header -->
            <div
              class="acc-group-header"
              :class="{ expanded: group.expanded }"
              @click="toggleGroup(group)"
            >
              <svg class="acc-arrow" :class="{ rotated: group.expanded }" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <path d="M6 4l4 4-4 4"></path>
              </svg>
              <div class="kh-av" :style="getAvatarStyle(group.kpiGroups[0]?.kpiType)">
                {{ getInitials(group.patientName) }}
              </div>
              <div class="acc-group-info">
                <div class="kh-name">{{ group.patientName }}</div>
                <div class="acc-group-meta">
                  <template v-if="filter.status === 0 || filter.isShared === true">{{ group.totalMessages }} tin nhắn</template>
                  <template v-else>{{ group.kpiGroups.length }} chủ đề · {{ group.totalMessages }} tin nhắn</template>
                </div>
              </div>
              <span v-if="group.pendingCount > 0" class="unread-dot" :title="`${group.pendingCount} chờ duyệt`"></span>
              <span v-if="group.hasSharedItem" class="acc-group-shared-icon" title="Shared Chat">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
              </span>
              <div class="kh-time">{{ formatTimeAgo(group.latestTime) }}</div>
            </div>

            <!-- Chờ duyệt / Chia sẻ (status=0 hoặc isShared): Flat list - không cần KpiType/Session -->
            <div v-if="group.expanded && (filter.status === 0 || filter.isShared === true)" class="acc-messages acc-messages-flat">
              <div
                v-for="msg in group.allMessages"
                :key="msg.id"
                class="acc-msg"
                :class="{ sel: selectedItem && selectedItem.id === msg.id }"
                @click.stop="selectItem(msg)"
              >
                <span class="kpi-dot" :style="{ background: getKpiDotColor(msg.kpiType) }"></span>
                <span class="acc-msg-text">{{ truncate(msg.aiResponse || msg.originalMessage, 50) }}</span>
                <span v-if="msg.kpiStatus === 0" class="acc-msg-kpi-tag" title="Có chỉ số chờ lưu">Chỉ số</span>
                <span v-if="msg.isShared" class="acc-msg-shared-tag" :title="'Shared by ' + msg.sharedByName">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
                </span>
                <span class="acc-msg-time">{{ formatTimeAgo(msg.createDatetime) }}</span>
              </div>
            </div>

            <!-- Đã gửi/Đã chỉnh sửa/Tất cả (không phải chờ duyệt và không phải chia sẻ): Group → KpiType → Messages -->
            <div v-if="group.expanded && filter.status !== 0 && filter.isShared !== true" class="acc-kpi-groups">
              <div
                v-for="kpiGroup in group.kpiGroups"
                :key="kpiGroup.kpiType"
                class="acc-kpi-group"
              >
                <!-- KpiType Header -->
                <div
                  class="acc-kpi-header"
                  :class="{ expanded: kpiGroup.expanded }"
                  @click.stop="toggleKpi(group.groupKey, kpiGroup)"
                >
                  <svg class="acc-arrow" :class="{ rotated: kpiGroup.expanded }" width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <path d="M6 4l4 4-4 4"></path>
                  </svg>
                  <span class="kpi-dot" :style="{ background: getKpiDotColor(kpiGroup.kpiType) }"></span>
                  <span class="acc-kpi-label">{{ kpiGroup.kpiLabel }}</span>
                  <span class="acc-kpi-meta">{{ kpiGroup.messages.length }} tin</span>
                </div>

                <!-- Messages Level -->
                <div v-if="kpiGroup.expanded" class="acc-messages">
                  <div
                    v-for="msg in kpiGroup.messages"
                    :key="msg.id"
                    class="acc-msg"
                    :class="{ sel: selectedItem && selectedItem.id === msg.id }"
                    @click.stop="selectItem(msg)"
                  >
                    <span class="acc-msg-status" :style="{ background: getStatusDotColor(msg.status) }"></span>
                    <span class="acc-msg-text">{{ truncate(msg.aiResponse || msg.originalMessage, 50) }}</span>
                    <span v-if="msg.kpiStatus === 0" class="acc-msg-kpi-tag" title="Có chỉ số chờ lưu">Chỉ số</span>
                    <span v-if="msg.isShared" class="acc-msg-shared-tag" :title="'Shared by ' + msg.sharedByName">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!loading && groupedData.length === 0" class="empty-list">
            <p>Không có dữ liệu</p>
          </div>

          <!-- Tải theo trang 20 nhóm; Tải thêm lấy tiếp nhóm cũ hơn -->
          <div v-if="!loading && groupedData.length < paging.total" class="kh-more">
            <LoadingButton class="btn btn-outline-primary btn-sm btn-block" :handler="loadMore">
              Tải thêm nhóm ({{ groupedData.length }}/{{ paging.total }})
            </LoadingButton>
          </div>
        </div>
      </div>

      <!-- Detail Panel -->
      <div class="detail" :class="{ 'mobile-visible': selectedItem }" v-if="selectedItem">
        <div class="d-header">
          <button class="d-back-btn d-md-none" @click="selectedItem = null; $router.replace({ query: { ...$route.query, id: undefined } })" aria-label="Quay lại">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M15 18l-6-6 6-6"></path>
            </svg>
          </button>
          <div class="kh-av d-av" :style="getAvatarStyle(selectedItem.kpiType)">
            {{ getInitials(selectedItem.patientName) }}
          </div>
          <div style="flex:1;min-width:0;">
            <div class="d-name">
              {{ selectedItem.patientName }}
              <span v-if="selectedItem.isShared" class="d-shared-tag">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
                Shared by {{ selectedItem.sharedByName }}
              </span>
            </div>
            <div class="d-meta">{{ selectedItem.recipient }} · {{ getKpiLabel(selectedItem.kpiType) }}</div>
          </div>
          <button class="d-chat-btn d-md-none" @click="openMobileChat" v-if="!mobileChatOpen">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
        </div>

        <!-- Shared Content Ask Banner -->
        <div v-if="selectedItem.isShared && selectedItem.sharedContentAsk" class="shared-content-banner">
          <div class="shared-content-header">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
            <span>Nội dung hỏi từ <strong>{{ selectedItem.sharedByName }}</strong></span>
          </div>
          <div class="shared-content-body">{{ getSharedQuestion(selectedItem.sharedContentAsk) }}</div>
        </div>

        <!-- Tin có chỉ số thì Thống kê thu gọn được (mặc định đóng) để nhường chỗ cho thẻ chỉ số;
             tin không có chỉ số thì hiện bình thường, không có nút thu gọn -->
        <div v-if="statsCollapsible" class="stats-collapse-toggle" @click="statsExpanded = !statsExpanded">
          <span>Thống kê</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            :style="{ transform: statsExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        <div :class="['stats-collapsible', { 'stats-collapsible--open': statsShown }]">
        <div class="stats-row">
          <div class="stat">
            <div class="stat__l">Trạng thái</div>
            <div class="stat__v" :style="{ color: getStatusColor(selectedItem.status) }">
              {{ getStatusLabel(selectedItem.status) }}
            </div>
            <div class="bar-bg">
              <div class="bar-fill" :style="{ width: selectedItem.status === 3 ? '100%' : '50%', background: getStatusColor(selectedItem.status) }"></div>
            </div>
          </div>
          <div class="stat">
            <div class="stat__l">Loại KPI</div>
            <div class="stat__v" :style="{ color: getKpiColor(selectedItem.kpiType) }">
              {{ getKpiLabel(selectedItem.kpiType) }}
            </div>
            <div class="bar-bg">
              <div class="bar-fill" style="width:60%;background:#378ADD;"></div>
            </div>
          </div>
          <div class="stat">
            <div class="stat__l">AI gửi Coach</div>
            <div class="stat__v" style="color: #185FA5;">
              {{ formatDateTime(selectedItem.createDatetime) }}
            </div>
            <div class="bar-bg">
              <div class="bar-fill" style="width: 40%; background: #378ADD;"></div>
            </div>
          </div>
          <div class="stat">
            <div class="stat__l">Coach phản hồi</div>
            <div class="stat__v" :style="{ color: selectedItem.reviewedAt ? '#0F6E56' : '#999' }">
              {{ selectedItem.reviewedAt ? formatDateTime(selectedItem.reviewedAt) : 'Chưa phản hồi' }}
            </div>
            <div class="bar-bg">
              <div class="bar-fill" :style="{ width: selectedItem.reviewedAt ? '100%' : '0%', background: '#1D9E75' }"></div>
            </div>
          </div>
        </div>

        <!-- Approval Stats -->
        <div class="approval-stats-row" v-if="approvalStats">
          <div class="approval-title">Thống kê</div>
          <div class="approval-bars">
            <div class="approval-item">
              <span class="approval-label">Approve thẳng</span>
              <div class="approval-track">
                <div class="approval-fill" :style="{ width: approvalStats.approvedDirectPercent + '%', background: '#1D9E75' }"></div>
              </div>
              <span class="approval-value" style="color:#0F6E56;">{{ approvalStats.approvedDirectPercent }}%</span>
            </div>
            <div class="approval-item">
              <span class="approval-label">Cần sửa</span>
              <div class="approval-track">
                <div class="approval-fill" :style="{ width: approvalStats.approvedEditedPercent + '%', background: '#BA7517' }"></div>
              </div>
              <span class="approval-value" style="color:#854F0B;">{{ approvalStats.approvedEditedPercent }}%</span>
            </div>
            <div class="approval-item">
              <span class="approval-label">TG phản hồi</span>
              <div class="approval-track">
                <div class="approval-fill" :style="{ width: getResponseTimePercent() + '%', background: '#378ADD' }"></div>
              </div>
              <span class="approval-value" style="color:#185FA5;">{{ getItemResponseTime() }}</span>
            </div>
          </div>
        </div>

        </div><!-- end stats-collapsible -->

        <div class="d-body">
          <!-- Compare Section (Top) -->
          <div class="compare-collapse-toggle d-md-none" @click="compareExpanded = !compareExpanded">
            <span>Khách hàng nhắn / Trợ lý ảo / Đã chỉnh sửa</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              :style="{ transform: compareExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>

          <!-- Tin nhắn gốc của khách hàng, để coach biết đang duyệt gợi ý cho câu hỏi nào -->
          <div
            class="original-message-box"
            :class="{ 'original-message-box--hidden': !compareExpanded }"
            v-if="selectedItem.originalMessage || decisionImages(selectedItem.imageUrl).length"
          >
            <div class="original-message-label">Khách hàng nhắn</div>
            <div v-if="decisionImages(selectedItem.imageUrl).length" class="patient-images">
              <img
                v-for="url in decisionImages(selectedItem.imageUrl)"
                :key="url"
                :src="url"
                class="patient-image"
                alt="Ảnh khách hàng gửi"
                @click="openMediaPreview(url, 'image')"
                @error="markBadImage(url)"
              />
            </div>
            <div v-if="selectedItem.originalMessage" class="original-message-text">{{ selectedItem.originalMessage }}</div>
          </div>

          <div class="compare-section" :class="{ 'compare-section--hidden': !compareExpanded }">
            <!-- AI Response (Left) -->
            <div class="compare-col">
              <div class="compare-title">
                <span class="compare-icon ai">AI</span>
                TRỢ LÝ ẢO
                <button class="chp-toggle-btn d-none d-md-flex" @click="toggleDesktopChat" :class="{ active: mobileChatOpen }" title="Lịch sử Zalo">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                  Tin nhắn
                </button>
              </div>
              <div class="compare-content" :class="{ 'no-edit': !selectedItem.aiResponse }">
                <!-- Tin không qua AI (rule bỏ qua): hiện nội dung gốc -->
                {{ selectedItem.aiResponse || `(Không qua AI) ${selectedItem.originalMessage || ''}` }}
              </div>
            </div>

            <!-- Edited Message (Right) -->
            <div class="compare-col">
              <div class="compare-title">
                <span class="compare-icon edited">✎</span>
                Đã chỉnh sửa
                <span v-if="selectedItem.editedMessage && selectedItem.editedMessage !== selectedItem.aiResponse" class="edit-badge">Có sửa</span>
              </div>
              <div class="compare-content" :class="{ 'no-edit': !selectedItem.editedMessage || selectedItem.editedMessage === selectedItem.aiResponse }">
                {{ selectedItem.editedMessage && selectedItem.editedMessage !== selectedItem.aiResponse ? selectedItem.editedMessage : '(Chưa chỉnh sửa)' }}
              </div>
            </div>
          </div>

          <!-- Chỉ số AI nhận diện - tách khỏi nút Gửi, lưu không nhắn gì cho khách hàng -->
          <kpi-editor
            v-if="kpiView"
            class="d-kpi-editor"
            :view="kpiView"
            collapsible
            @handled="onKpiHandled"
          />

          <!-- Zalo Chat History (desktop) -->
          <div class="chp-wrap" v-if="desktopChatOpen">
            <div class="chp-header">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
              <span class="chp-title">Lịch sử Zalo</span>
              <span class="chp-group-name" v-if="chatHistoryGroupName">{{ chatHistoryGroupName }}</span>
            </div>
            <div class="chp-body" ref="chatHistoryBody">
              <div v-if="chatHistoryLoading" class="chp-loading">
                <span class="chp-spinner"></span> Đang tải...
              </div>
              <div v-else-if="chatHistoryMessages.length === 0" class="chp-empty">
                Không có tin nhắn nào
              </div>
              <div v-else class="chp-messages">
                <div
                  v-for="msg in chatHistoryMessages"
                  :key="msg.id"
                  class="chp-msg"
                  :class="msg.senderType === 'oa' ? 'chp-msg--oa' : 'chp-msg--user'"
                >
                  <div class="chp-msg__avatar" v-if="msg.senderType !== 'oa'">
                    {{ (msg.userName || '?').charAt(0).toUpperCase() }}
                  </div>
                  <div class="chp-msg__bubble">
                    <div class="chp-msg__name" v-if="msg.senderType !== 'oa'">{{ msg.userName }}</div>
                    <div v-if="msg.uploading" class="chp-msg__uploading">
                      <span class="btn-spinner"></span> Đang gửi...
                    </div>
                    <img v-else-if="msg.imageUrl && !msg.imageBroken" :src="msg.imageUrl" class="chp-msg__img" @click="openMediaPreview(msg.imageUrl, 'image')" @error="onImageError(msg)" />
                    <div v-else-if="msg.imageBroken" class="chp-msg__img-expired">Ảnh đã hết hạn trên Zalo</div>
                    <video v-else-if="msg.videoUrl" :src="msg.videoUrl" class="chp-msg__video" @click="openMediaPreview(msg.videoUrl, 'video')" />
                    <a v-else-if="msg.fileUrl" :href="msg.fileUrl" target="_blank" class="chp-msg__file" @click.stop>
                      <span class="chp-msg__file-icon">{{ getFileIcon(msg.fileUrl) }}</span>
                      <span class="chp-msg__file-name">{{ msg.fileName || getFileName(msg.fileUrl) }}</span>
                    </a>
                    <!-- Tin anh/file kem chu: chu nam duoi hinh -->
                    <div
                      v-if="msg.message"
                      class="chp-msg__text"
                      :class="{ 'chp-msg__text--caption': msg.imageUrl || msg.videoUrl || msg.fileUrl }"
                    >{{ msg.message }}</div>
                    <div class="chp-msg__time">{{ formatChatTime(msg.timestamp) }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="chat-direct-input">
              <!-- File dang cho gui cung text -->
              <div v-if="pendingFile" class="chat-pending">
                <img v-if="pendingPreviewUrl" :src="pendingPreviewUrl" class="chat-pending__thumb" alt="" />
                <span v-else class="chat-pending__icon">{{ getFileIcon(pendingFile.name) }}</span>
                <span class="chat-pending__name">{{ pendingFile.name }}</span>
                <button type="button" class="chat-pending__remove" @click="clearPendingFile" title="Bo file dinh kem">&times;</button>
              </div>
              <label class="chat-attach-btn" :class="{ disabled: sendingDirect }" title="Đính kèm file/ảnh">
                <input type="file" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" @change="handleAttachment" :disabled="sendingDirect" style="display:none" />
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                </svg>
              </label>
              <b-form-textarea
                class="chat-direct-textarea"
                v-model="directMessage"
                :maxlength="ZALO_MAX_CHARS"
                :placeholder="pendingFile ? 'Nhập chú thích gửi kèm (tuỳ chọn)...' : 'Nhập tin nhắn...'"
                rows="2"
                @keydown.enter.exact.prevent="sendDirectMessage"
              ></b-form-textarea>
              <button class="chat-direct-send" @click="sendDirectMessage" :disabled="sendingDirect || (!directMessage.trim() && !pendingFile) || directMessage.length > ZALO_MAX_CHARS">
                <span v-if="sendingDirect" class="btn-spinner"></span>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>
              </button>
            </div>
          </div>

          <!-- Edit & Send Section (Bottom) -->
          <div class="edit-send-section">
            <!-- Status bar if sent/rejected -->
            <div class="send-status" v-if="selectedItem.status === 3 || selectedItem.status === 2">
              <span class="status-icon" :class="selectedItem.status === 3 ? 'success' : 'danger'">
                {{ selectedItem.status === 3 ? '✓' : '✗' }}
              </span>
              <span>{{ selectedItem.status === 3 ? 'Đã gửi' : 'Đã từ chối' }}</span>
              <span class="status-time" v-if="selectedItem.sentAt">· {{ formatDateTime(selectedItem.sentAt) }}</span>
              <span class="edit-percent-badge" v-if="selectedItem.status === 3" :class="getEditPercentClass()">
                {{ getItemEditPercent() }}% thay đổi
              </span>
            </div>

            <!-- Textarea -->
            <div class="edit-textarea-wrap">
              <div class="mobile-textarea-actions d-md-none">
                <button class="btn btn-outline-secondary" @click="editMessage = selectedItem.aiResponse">
                  <span class="svg-icon svg-icon-sm">
                    <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" />
                  </span>
                  Khôi phục
                </button>
                <div class="spacer"></div>
                <button v-if="selectedItem && selectedItem.status === 0" class="btn btn-outline-primary ml-2" @click="openShareModal">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="18" cy="5" r="3"></circle>
                    <circle cx="6" cy="12" r="3"></circle>
                    <circle cx="18" cy="19" r="3"></circle>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                  </svg>
                  Chia sẻ
                </button>
                <button v-if="selectedItem && selectedItem.status === 0" class="btn btn-skip ml-2" @click="handleSkip" :disabled="skipping">
                  <span v-if="skipping" class="btn-spinner"></span>
                  {{ skipping ? 'Đang xử lý...' : 'Bỏ qua' }}
                </button>
                <button class="btn btn-success ml-2" @click="handleSend" :disabled="sending || !editMessage.trim() || editTooLong">
                  <span v-if="!sending" class="svg-icon svg-icon-sm">
                    <inline-svg src="/media/svg/icons/Neolex/Basic/send.svg" />
                  </span>
                  <span v-if="sending" class="btn-spinner"></span>
                  {{ sending ? 'Đang gửi...' : (selectedItem.status === 3 || selectedItem.status === 2 ? 'Gửi lại' : 'Gửi') }}
                </button>
              </div>
              <b-form-textarea
                class="edit-textarea"
                v-model="editMessage"
                :maxlength="ZALO_MAX_CHARS"
                placeholder="Chỉnh sửa nội dung tin nhắn..."
                rows="5"
                @keydown.ctrl.enter="handleSend"
                @focus="textareaFocused = true"
                @blur="textareaFocused = false"
                :style="mobileTextareaHeight ? { minHeight: mobileTextareaHeight, height: mobileTextareaHeight, overflowY: 'auto' } : {}"
              ></b-form-textarea>
              <div class="edit-charcount" :class="{ 'near-limit': editMessage.length > ZALO_MAX_CHARS - 100, 'over-limit': editTooLong }">
                <template v-if="editTooLong">Vượt {{ editMessage.length - ZALO_MAX_CHARS }} ký tự · </template>
                {{ editMessage.length }}/{{ ZALO_MAX_CHARS }}
              </div>
            </div>

            <div class="d-md-none">
              <div v-if="selectedItem && selectedItem.status === 0" class="review-feedback review-feedback--mobile">
                <b-form-select v-model="reviewReason" :options="reviewReasonOptions" size="sm" class="review-feedback__reason" />
                <b-form-input v-model="reviewNote" size="sm" maxlength="1000" placeholder="AI nên trả lời thế nào? (không ghi tên, SĐT khách)" />
              </div>
            </div>

            <!-- Desktop action buttons -->
            <div class="edit-send-actions d-none d-md-flex">
              <button class="btn btn-outline-secondary ml-2" @click="editMessage = selectedItem.aiResponse">
                <span class="svg-icon svg-icon-sm">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" />
                </span>
                Khôi phục
              </button>
              <div v-if="selectedItem && selectedItem.status === 0" class="review-feedback">
                <b-form-select v-model="reviewReason" :options="reviewReasonOptions" size="sm" class="review-feedback__reason" />
                <b-form-input v-model="reviewNote" size="sm" maxlength="1000" placeholder="AI nên trả lời thế nào? (không ghi tên, SĐT khách)" />
              </div>
              <div v-else class="spacer"></div>
              <button v-if="selectedItem && selectedItem.status === 0" class="btn btn-outline-primary ml-2" @click="openShareModal">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
                Chia sẻ
              </button>
              <button v-if="selectedItem && selectedItem.status === 0" class="btn btn-skip ml-2" @click="handleSkip" :disabled="skipping">
                <span v-if="skipping" class="btn-spinner"></span>
                {{ skipping ? 'Đang xử lý...' : 'Bỏ qua' }}
              </button>
              <button class="btn btn-success ml-2" @click="handleSend" :disabled="sending || !editMessage.trim() || editTooLong">
                <svg v-if="!sending" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                </svg>
                <span v-if="sending" class="btn-spinner"></span>
                {{ sending ? 'Đang gửi...' : (selectedItem.status === 3 || selectedItem.status === 2 ? 'Gửi lại' : 'Gửi') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Floating chat button (mobile only) -->
        <button class="mobile-chat-fab" @click="mobileChatOpen = true" v-if="false">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      </div>

      <!-- Empty Detail State - Full Dashboard -->
      <div class="detail detail-dashboard" v-else>
        <div class="dashboard-header">
          <h4 class="dashboard-title">Dashboard AI Coach</h4>
          <p class="dashboard-subtitle">Tổng quan hoạt động Decision AI Coach</p>
        </div>

        <div class="dashboard-stats-grid">
          <div class="dash-stat-card pending">
            <div class="dash-stat-icon">⏳</div>
            <div class="dash-stat-info">
              <div class="dash-stat-value">{{ stats.totalPending }}</div>
              <div class="dash-stat-label">Chờ duyệt</div>
            </div>
          </div>
          <div class="dash-stat-card edited">
            <div class="dash-stat-icon">✎</div>
            <div class="dash-stat-info">
              <div class="dash-stat-value">{{ stats.totalEdited }}</div>
              <div class="dash-stat-label">Đã chỉnh sửa</div>
            </div>
          </div>
          <div class="dash-stat-card sent">
            <div class="dash-stat-icon">✓</div>
            <div class="dash-stat-info">
              <div class="dash-stat-value">{{ stats.totalSent }}</div>
              <div class="dash-stat-label">Đã gửi</div>
            </div>
          </div>
          <div class="dash-stat-card today">
            <div class="dash-stat-icon">📅</div>
            <div class="dash-stat-info">
              <div class="dash-stat-value">{{ stats.totalToday }}</div>
              <div class="dash-stat-label">Hôm nay</div>
            </div>
          </div>
          <div class="dash-stat-card shared">
            <div class="dash-stat-icon">🔗</div>
            <div class="dash-stat-info">
              <div class="dash-stat-value">{{ stats.totalShared }}</div>
              <div class="dash-stat-label">Chia sẻ</div>
            </div>
          </div>
        </div>

        <div class="dashboard-section">
          <div class="section-header">
            <h5>Nhóm chỉ định cho coach</h5>
            <span class="section-badge">{{ myEnabledGroups.length }} nhóm</span>
          </div>
          <div class="enabled-groups-list" v-if="myEnabledGroups.length > 0">
            <div class="enabled-group-chip" v-for="groupId in myEnabledGroups.slice(0, 10)" :key="groupId" :title="groupId">
              {{ getGroupName(groupId) }}
            </div>
            <div class="enabled-group-chip more" v-if="myEnabledGroups.length > 10">
              +{{ myEnabledGroups.length - 10 }} khác
            </div>
          </div>
          <div class="no-groups-msg" v-else>
            <p>{{ canManageAll ? 'Chưa có nhóm nào được cấu hình. Nhấn "Cấu hình nhóm" để bắt đầu.' : 'Bạn chưa được gán nhóm nào.' }}</p>
          </div>
        </div>

        <div class="dashboard-hint">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
          </svg>
          <span>Chọn một tin nhắn từ danh sách bên trái để xem chi tiết và duyệt</span>
        </div>
      </div>
    </div>

    <!-- Mobile Chat Overlay (outside shell, fills full coach-cms height) -->
    <transition name="chat-slide">
      <div v-if="selectedItem && mobileChatOpen" class="mobile-chat-overlay chat-overlay">
        <div class="mobile-chat-header">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
          <span class="mobile-chat-title">Lịch sử Zalo</span>
          <span class="mobile-chat-group" v-if="chatHistoryGroupName">{{ chatHistoryGroupName }}</span>
          <button class="mobile-chat-close" @click="mobileChatOpen = false">✕</button>
        </div>
        <div class="mobile-chat-body" ref="mobileChatBody">
          <div v-if="chatHistoryLoading" class="chp-loading">
            <span class="chp-spinner"></span> Đang tải...
          </div>
          <div v-else-if="chatHistoryMessages.length === 0" class="chp-empty">Không có tin nhắn nào</div>
          <div v-else class="chp-messages">
            <div
              v-for="msg in chatHistoryMessages"
              :key="msg.id"
              class="chp-msg"
              :class="msg.senderType === 'oa' ? 'chp-msg--oa' : 'chp-msg--user'"
            >
              <div class="chp-msg__avatar" v-if="msg.senderType !== 'oa'">
                {{ (msg.userName || '?').charAt(0).toUpperCase() }}
              </div>
              <div class="chp-msg__bubble">
                <div class="chp-msg__name" v-if="msg.senderType !== 'oa'">{{ msg.userName }}</div>
                <img v-if="msg.imageUrl && !msg.imageBroken" :src="msg.imageUrl" class="chp-msg__img" @click="openMediaPreview(msg.imageUrl, 'image')" @error="onImageError(msg)" />
                <div v-else-if="msg.imageBroken" class="chp-msg__img-expired">Ảnh đã hết hạn trên Zalo</div>
                <video v-else-if="msg.videoUrl" :src="msg.videoUrl" class="chp-msg__video" @click="openMediaPreview(msg.videoUrl, 'video')" />
                <a v-else-if="msg.fileUrl" :href="msg.fileUrl" target="_blank" class="chp-msg__file" @click.stop>
                  <span class="chp-msg__file-icon">{{ getFileIcon(msg.fileUrl) }}</span>
                  <span class="chp-msg__file-name">{{ msg.fileName || getFileName(msg.fileUrl) }}</span>
                </a>
                <!-- Tin anh/file kem chu: chu nam duoi hinh -->
                <div
                  v-if="msg.message"
                  class="chp-msg__text"
                  :class="{ 'chp-msg__text--caption': msg.imageUrl || msg.videoUrl || msg.fileUrl }"
                >{{ msg.message }}</div>
                <div class="chp-msg__time">{{ formatChatTime(msg.timestamp) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="chat-direct-input">
          <!-- File dang cho gui cung text -->
          <div v-if="pendingFile" class="chat-pending">
            <img v-if="pendingPreviewUrl" :src="pendingPreviewUrl" class="chat-pending__thumb" alt="" />
            <span v-else class="chat-pending__icon">{{ getFileIcon(pendingFile.name) }}</span>
            <span class="chat-pending__name">{{ pendingFile.name }}</span>
            <button type="button" class="chat-pending__remove" @click="clearPendingFile" title="Bo file dinh kem">&times;</button>
          </div>
          <b-form-textarea
            class="chat-direct-textarea"
            v-model="directMessage"
                :maxlength="ZALO_MAX_CHARS"
            :placeholder="pendingFile ? 'Nhập chú thích gửi kèm (tuỳ chọn)...' : 'Nhập tin nhắn...'"
            rows="2"
            @keydown.enter.exact.prevent="sendDirectMessage"
          ></b-form-textarea>
          <label class="chat-attach-btn" :class="{ disabled: sendingDirect }" title="Đính kèm file/ảnh">
            <input type="file" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" @change="handleAttachment" :disabled="sendingDirect" style="display:none" />
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
          </label>
          <button class="chat-direct-send" @click="sendDirectMessage" :disabled="sendingDirect || (!directMessage.trim() && !pendingFile) || directMessage.length > ZALO_MAX_CHARS">
            <span v-if="sendingDirect" class="btn-spinner"></span>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>
          </button>
        </div>
      </div>
    </transition>

    <!-- Mobile Filter Panel (fixed, outside shell) -->
    <transition name="mtb-drop">
      <div v-if="mobileFilterOpen" class="mtb-panel d-md-none">
        <div class="mtb-section">
          <div class="mtb-label">TRẠNG THÁI</div>
          <div class="mtb-chips">
            <div class="nav" :class="{ on: filter.status === null && filter.isShared !== true }" @click="setStatusFilter(null)"><span class="ndot" style="background:#888;"></span>Tất cả</div>
            <div class="nav" :class="{ on: filter.status === 0 }" @click="setStatusFilter(0)"><span class="ndot" style="background:#BA7517;"></span>Chờ duyệt<span class="nc" v-if="stats.totalPending">{{ stats.totalPending }}</span></div>
            <div class="nav" :class="{ on: filter.status === 1 }" @click="setStatusFilter(1)"><span class="ndot" style="background:#378ADD;"></span>Đã chỉnh sửa</div>
            <div class="nav" :class="{ on: filter.status === 3 }" @click="setStatusFilter(3)"><span class="ndot" style="background:#1D9E75;"></span>Đã gửi</div>
            <div class="nav" :class="{ on: filter.status === 4 }" @click="setStatusFilter(4)"><span class="ndot" style="background:#6B7280;"></span>Bỏ qua<span class="nc nc-gray" v-if="stats.totalSkipped">{{ stats.totalSkipped }}</span></div>
            <div class="nav" :class="{ on: filter.isShared === true }" @click="setSharedFilter(true)"><span class="ndot" style="background:#9B59B6;"></span>Chia sẻ<span class="nc nc-purple" v-if="stats.totalShared">{{ stats.totalShared }}</span></div>
          </div>
        </div>
        <div class="mtb-section">
          <div class="mtb-label">CHỦ ĐỀ</div>
          <div class="mtb-chips">
            <div class="nav" :class="{ on: filter.kpiType === null }" @click="setKpiFilter(null)"><span class="ndot" style="background:#888;"></span>Tất cả</div>
            <div v-for="kpi in kpiFilterOptions" :key="kpi.value" class="nav" :class="{ on: filter.kpiType === kpi.value }" @click="setKpiFilter(kpi.value)"><span class="ndot" :style="{ background: kpi.dot }"></span>{{ kpi.label }}</div>
          </div>
        </div>
        <div class="mtb-section" v-if="canManageAll">
          <div class="mtb-label">HEALTH COACH</div>
          <div class="mtb-chips">
            <basic-select label="" placeholder="Tất cả Coach" name="coachFilter" :options="coachOptions" track-by="value" value-label="text" :value.sync="selectedCoachOption" @input="onCoachSelect" />
          </div>
        </div>
      </div>
    </transition>

    <!-- Mobile Search Panel (fixed, outside shell) -->
    <transition name="mtb-drop">
      <div v-if="mobileSearchOpen" class="mtb-search-panel d-md-none">
        <div class="search-mini search-mini--full">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="6.5" cy="6.5" r="5"></circle><path d="M10.5 10.5l3.5 3.5"></path></svg>
          <b-form-input type="text" v-model="searchQuery" placeholder="Tìm nhóm..." @input="onSearchDebounce" />
          <button v-if="searchQuery" @click="searchQuery = ''; onSearchDebounce()" class="mtb-clear">✕</button>
        </div>
      </div>
    </transition>

    <!-- Media Preview Modal -->
    <div v-if="previewMedia" class="media-preview-overlay" @click.self="closeMediaPreview">
      <div class="media-preview-content">
        <button class="media-preview-close" @click="closeMediaPreview">✕</button>
        <img v-if="previewMedia.type === 'image'" :src="previewMedia.url" class="media-preview-img" />
        <video v-else-if="previewMedia.type === 'video'" :src="previewMedia.url" class="media-preview-video" controls autoplay />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as signalR from '@microsoft/signalr';
import Mgr from '@/core/services/security.service';
import decisionAiCoachService from './services/decisionAiCoachService';
import KpiEditor from './components/KpiEditor';
import CoachActiveHoursModal from './components/CoachActiveHoursModal';
import { GLUCOSE_TIME_FRAMES, MEAL_TIME_FRAMES, DEFAULT_MEAL_TIME_FRAME } from './constants/kpiTimeFrames';
import { PAGE_CODE, PERMISSION_TYPE } from '@/core/plugins/constants';
import { hasPermission } from '@/core/services/permission.service';

// Nguồn duy nhất cho nhãn + màu chủ đề. Trước đây 6 bảng rải rác nên thêm chủ đề là sót.
const KPI_META = {
  blood_sugar:    { label: 'Đường huyết', dot: '#E24B4A', color: '#A32D2D', bg: '#FCEBEB', border: '#F09595', avatarBg: '#FCEBEB', avatarColor: '#A32D2D' },
  nutrition:      { label: 'Dinh dưỡng',  dot: '#639922', color: '#3B6D11', bg: '#EAF3DE', border: '#C0DD97', avatarBg: '#E1F5EE', avatarColor: '#0F6E56' },
  weight:         { label: 'Cân nặng',    dot: '#7F77DD', color: '#3C3489', bg: '#EEEDFE', border: '#AFA9EC', avatarBg: '#EEEDFE', avatarColor: '#3C3489' },
  hba1c:          { label: 'HbA1c',       dot: '#BA7517', color: '#854F0B', bg: '#FAEEDA', border: '#FAC775', avatarBg: '#FAEEDA', avatarColor: '#854F0B' },
  blood_pressure: { label: 'Huyết áp',    dot: '#378ADD', color: '#185FA5', bg: '#E6F1FB', border: '#85B7EB', avatarBg: '#E6F1FB', avatarColor: '#185FA5' },
  normal_chat:    { label: 'Trò chuyện',  dot: '#888780', color: '#5F5E5A', bg: '#F1EFE8', border: '#D3D1C7', avatarBg: '#F1EFE8', avatarColor: '#5F5E5A' },
  non_target_chat: { label: 'KH không chỉ định', dot: '#B4B2A9', color: '#5F5E5A', bg: '#F1EFE8', border: '#D3D1C7', avatarBg: '#F1EFE8', avatarColor: '#5F5E5A' },
};

const KPI_FALLBACK = { label: 'Khác', dot: '#888780', color: '#5F5E5A', bg: '#F1EFE8', border: '#D3D1C7', avatarBg: '#E1F5EE', avatarColor: '#0F6E56' };

// bmi gộp vào weight (record cũ vẫn còn giá trị này)
const KPI_ALIAS = { bmi: 'weight' };

function kpiMeta(kpiType) {
  const key = KPI_ALIAS[kpiType] || kpiType;
  return KPI_META[key] || KPI_FALLBACK;
}


export default {
  name: 'DecisionAiCoachList',
  components: { KpiEditor, CoachActiveHoursModal },
  data() {
    return {
      loading: false,
      data: [],
      paging: { page: 1, pageSize: 20, total: 0 },
      loadingMore: false,
      filter: { status: 0, kpiType: null, isShared: null },
      sort: { by: 'createDatetime', order: 'desc' },
      stats: { totalPending: 0, totalEdited: 0, totalRejected: 0, totalSent: 0, totalSkipped: 0, totalShared: 0 },
      selectedItem: null,
      reviewReason: null,
      reviewNote: '',
      reviewReasonOptions: [
        { value: null, text: 'Lý do bỏ qua / sửa...' },
        { value: 'too_long', text: 'Quá dài, thừa ý' },
        { value: 'wrong_content', text: 'Sai nội dung, sai chuyên môn' },
        { value: 'wrong_image', text: 'Đọc sai ảnh' },
        { value: 'missing_info', text: 'Thiếu thực đơn, thông tin khách' },
        { value: 'old_context', text: 'Nhầm ngữ cảnh cũ' },
        { value: 'app_or_schedule', text: 'Không biết cách dùng app, lịch hẹn' },
        { value: 'wrong_tone', text: 'Sai giọng, xưng hô' },
        { value: 'answered_elsewhere', text: 'Đã trả lời chung ở tin khác' },
        { value: 'no_reply_needed', text: 'Không cần trả lời' },
        { value: 'needs_human', text: 'Coach tự xử lý, cần người' },
        { value: 'other', text: 'Khác' },
      ],
      editMessage: '',
      // Zalo bỏ nguyên tin nếu vượt 2000 ký tự (lỗi -210)
      ZALO_MAX_CHARS: 2000,
      coachNote: '',
      sending: false,
      skipping: false,
      // Chỉ số AI nhận diện của tin đang mở (KpiView), KpiEditor lo phần tick / sửa / lưu
      kpiView: null,
      // Modal tổng quan chỉ số theo khách hàng
      showKpiOverview: false,
      showActiveHours: false,
      // Link ảnh đã lỗi (hết hạn / không tải được) -> ẩn, không để ảnh vỡ
      badImages: {},
      // Tin neo của khách hàng đang chọn - BE lấy patient + account của tin này để lọc
      overviewAnchorId: null,
      overviewPatientKey: null,
      overviewPatients: [],
      overviewPatientsLoading: false,
      overviewPatientSearch: '',
      overviewPendingOnly: true,
      overviewPatientName: '',
      overviewItems: [],
      overviewLoading: false,
      overviewDays: 30,
      overviewTab: 'blood_sugar',
      overviewStatus: 'all',
      overviewBusyId: null,
      // Dòng đang mở rộng trong modal: 'view' chỉ xem, 'edit' mở KpiEditor ở chế độ sửa
      overviewExpandedId: null,
      overviewExpandMode: 'view',
      // Tin trong session theo sessionId -> { loading, items }
      sessionThreads: {},
      // Món đang sửa dở trong KpiEditor theo id tin -> cột Món / Calo của dòng hiện theo
      kpiDrafts: {},
      // Chỉ 2 chủ đề này có lưu payload chỉ số
      overviewTabs: [
        { key: 'blood_sugar', label: 'Đường huyết' },
        { key: 'nutrition', label: 'Dinh dưỡng' },
      ],
      searchQuery: '',
      searchTimeout: null,
      approvalStats: null,
      expandedGroups: {},
      expandedKpis: {},
      expandedDates: {},
      // Group config
      showGroupConfigModal: false,
      loadingGroups: false,
      allGroups: [],
      // Hiện nút Văn phong trong modal cấu hình nhóm. Tạm tắt tới khi xong luồng duyệt văn phong + chuông thông báo
      showStyleCoachPicker: false,
      // Văn phong theo chủ đề: [{ key, label, coaches: [{ coachId, name, samples }] }], groupId -> { kpiType: coachId }
      styleTopics: [],
      groupTopicStyles: {},
      styleMinSamples: 1,
      styleMaxPairs: 5,
      // Câu mẫu theo `${coachId}|${kpiType}` -> { loading, items }
      styleSamples: {},
      expandedSampleTopic: null,
      // Bản nháp trong modal văn phong, cùng dạng groupTopicStyles; bấm Lưu mới gửi BE
      styleDraft: {},
      savingStyles: false,
      showStyleModal: false,
      styleGroupId: null,
      styleGroupSearch: '',

      groupSearchQuery: '',
      // group_id -> số customer_receives gán vào nhóm; null = chưa tải / lỗi
      groupCustomerCounts: null,
      hideUnassignedGroups: true,
      // Popup chọn người AI theo dõi; isNew = mở do vừa tick bật AI (Huỷ thì bỏ tick)
      aiTarget: { show: false, group: null, candidates: [], loading: false, choice: '', isNew: false },
      // groupId -> zaloUserId ('' = tất cả), gửi BE khi bấm Lưu
      pendingAiTargets: {},
      // groupId -> tên người đang theo dõi để hiện trên dòng nhóm
      aiTargetNames: {},
      // groupId -> số khách (không tính coach/OA) đã nhắn; nút chọn khách hàng chỉ hiện khi >= 2
      aiCandidateCounts: {},
      customerReceivesData: [], // For mapping groups to coaches
      aiEnabledGroups: [],
      selectedAIGroupIds: [],
      // Coach filter
      coaches: [],
      selectedCoachId: null,
      selectedCoachOption: null,
      currentUserId: null,
      mobileSidebarExpanded: false,
      mobileFilterOpen: false,
      mobileSearchOpen: false,
      statsExpanded: false,
      compareExpanded: false,
      textareaFocused: false,
      // Chat history
      mobileChatOpen: false,
      desktopChatOpen: false,
      chatHistoryLoading: false,
      chatHistoryMessages: [],
      chatHistoryGroupName: '',
      // SignalR
      hubConnection: null,
      // Share
      showShareModal: false,
      loadingCoaches: false,
      sharing: false,
      availableCoaches: [],
      selectedShareCoachIds: [],
      contentAsk: '',
      // Tabs
      activeTab: 'review',
      directMessage: '',
      sendingDirect: false,
      uploadingMedia: false,
      // File da chon nhung chua gui - cho coach nhap chu roi gui chung mot tin nhan
      pendingFile: null,
      pendingPreviewUrl: null,
      // Media preview
      previewMedia: null,
    };
  },
  computed: {
    ...mapGetters(['currentUser']),
    // Tin của tab đang chọn, trước khi lọc trạng thái - dùng cho số đếm ở bộ lọc và footer
    overviewInTab() {
      return this.overviewItems.filter(v => this.overviewTabOf(v) === this.overviewTab);
    },
    overviewFiltered() {
      if (this.overviewStatus === 'all') return this.overviewInTab;
      return this.overviewInTab.filter(v => v.kpiStatus === this.overviewStatus);
    },
    kpiFilterOptions() {
      return Object.keys(KPI_META).map(value => ({ value, label: KPI_META[value].label, dot: KPI_META[value].dot }));
    },
    // Nhóm hiện trong modal văn phong = nhóm đang được tick bật AI trong modal Cấu hình nhóm
    styleGroupsFiltered() {
      const q = (this.styleGroupSearch || '').toLowerCase();
      return this.allGroups
        .filter(g => this.selectedAIGroupIds.includes(g.group_id))
        .filter(g => !q || (g.name || '').toLowerCase().includes(q) || (g.group_id || '').toLowerCase().includes(q));
    },
    // Các ô nhóm × chủ đề mà bản nháp khác với đã lưu
    styleChanges() {
      const changes = [];
      const groupIds = new Set([...Object.keys(this.styleDraft), ...Object.keys(this.groupTopicStyles)]);
      groupIds.forEach(groupId => {
        const draft = this.styleDraft[groupId] || {};
        const saved = this.groupTopicStyles[groupId] || {};
        new Set([...Object.keys(draft), ...Object.keys(saved)]).forEach(kpiType => {
          const d = draft[kpiType];
          const s = saved[kpiType];
          const same = (!d && !s)
            || (d && s && d.coachId === s.coachId && d.pairIds.join() === s.pairIds.join());
          if (!same) changes.push({ groupId, kpiType, coachId: d ? d.coachId : null, pairIds: d ? d.pairIds : [] });
        });
      });
      return changes;
    },
    styleGroupName() {
      const g = this.allGroups.find(x => x.group_id === this.styleGroupId);
      return (g && g.name) || this.styleGroupId || '';
    },
    overviewPatientsFiltered() {
      const q = (this.overviewPatientSearch || '').toLowerCase();
      if (!q) return this.overviewPatients;
      return this.overviewPatients.filter(pt =>
        (pt.patientName || '').toLowerCase().includes(q) || (pt.recipient || '').toLowerCase().includes(q));
    },
    // maxlength không chặn bản nháp AI điền sẵn đã quá dài, nên khoá nút Gửi theo độ dài thật
    editTooLong() {
      return (this.editMessage || '').length > this.ZALO_MAX_CHARS;
    },
    // Chỉ tin có chỉ số mới cho thu gọn Thống kê; tin thường luôn hiện
    statsCollapsible() {
      return !!(this.selectedItem && this.selectedItem.hasKpiPayload);
    },
    statsShown() {
      return !this.statsCollapsible || this.statsExpanded;
    },
    mobileTextareaHeight() {
      if (typeof window === 'undefined' || window.innerWidth >= 768) return null;
      let h = 62;
      if (this.statsShown) h -= 22;
      if (this.compareExpanded) h -= 40;
      return `${Math.max(h, 16)}vh`;
    },
    // Role code có hậu tố theo phòng ban (Leader_Technology, Manager_ChamSoc) nên
    // chỉ lấy phần trước dấu "_". Gom cả accountRoles lẫn roles vì tuỳ API trả về field nào.
    userRoleNames() {
      const accountRoles = this.currentUser?.accountRoles || this.currentUser?.AccountRoles || [];
      const fromAccountRoles = accountRoles.map(r => {
        const role = r.role || r.Role;
        return role?.code || role?.Code || '';
      });
      const fromRoles = this.currentUser?.roles || this.currentUser?.Roles || [];
      return [...fromAccountRoles, ...fromRoles]
        .filter(Boolean)
        .map(name => name.split('_')[0].toLowerCase());
    },
    isAdmin() {
      return (
        this.userRoleNames.includes('admin') ||
        this.userRoleNames.includes('superadmin') ||
        this.userRoleNames.includes('administrator')
      );
    },
    isManager() {
      return this.userRoleNames.includes('manager');
    },
    // Admin, Manager, hoặc role được cấp quyền Cập nhật trên chính tính năng này.
    // Chỉ có quyền Xem thì không tính - vẫn chỉ thấy dữ liệu của mình.
    canManageAll() {
      return (
        this.isAdmin ||
        this.isManager ||
        hasPermission(PAGE_CODE.DECISION_AI_COACH, [PERMISSION_TYPE.UPDATE])
      );
    },
    coachOptions() {
      return this.coaches.map(c => ({
        value: c.id,
        text: c.fullName
      }));
    },
    // Không có quyền quản lý thì chỉ thấy nhóm mình phụ trách
    myEnabledGroups() {
      if (this.canManageAll) {
        return this.aiEnabledGroups;
      }
      const myCoachId = this.currentUser?.id || this.currentUser?.Id;
      if (!myCoachId) return [];
      // Filter to groups where this coach is assigned
      const myGroupIds = this.customerReceivesData
        .filter(cr => (cr.coachId || cr.CoachId) === myCoachId)
        .map(cr => cr.zaloGroupId || cr.ZaloGroupId)
        .filter(Boolean);
      return this.aiEnabledGroups.filter(groupId => myGroupIds.includes(groupId));
    },
    userInitials() {
      if (this.currentUser && this.currentUser.fullName) {
        return this.currentUser.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
      }
      return 'AD';
    },
    groupedData() {
      if (!this.data || this.data.length === 0) return [];

      const groupMap = {};

      this.data.forEach(item => {
        const groupKey = item.recipient || item.patientId || item.patientName || 'unknown';

        if (!groupMap[groupKey]) {
          groupMap[groupKey] = {
            groupKey,
            patientName: item.patientName || 'Unknown',
            recipient: item.recipient,
            patientId: item.patientId,
            expanded: this.expandedGroups[groupKey] ?? false,
            kpiGroups: {},
            allMessages: [],
            totalMessages: 0,
            pendingCount: 0,
            latestTime: item.createDatetime,
            hasSharedItem: false,
          };
        }

        const group = groupMap[groupKey];
        group.allMessages.push(item);
        group.totalMessages++;
        if (item.status === 0) group.pendingCount++;
        if (item.isShared) group.hasSharedItem = true;
        if (new Date(item.createDatetime) > new Date(group.latestTime)) {
          group.latestTime = item.createDatetime;
        }

        // Group by kpiType (chủ đề) - đã bỏ tầng session, tin nhắn nằm thẳng dưới chủ đề
        const kpiType = KPI_ALIAS[item.kpiType] || item.kpiType || 'other';
        if (!group.kpiGroups[kpiType]) {
          group.kpiGroups[kpiType] = {
            kpiType,
            kpiLabel: this.getKpiLabel(kpiType),
            messages: [],
            totalMessages: 0,
            pendingCount: 0,
            latestTime: item.createDatetime,
            expanded: this.expandedKpis?.[`${groupKey}_${kpiType}`] ?? false,
          };
        }

        const kpiGroup = group.kpiGroups[kpiType];
        kpiGroup.messages.push(item);
        kpiGroup.totalMessages++;
        if (item.status === 0) kpiGroup.pendingCount++;
        if (new Date(item.createDatetime) > new Date(kpiGroup.latestTime)) {
          kpiGroup.latestTime = item.createDatetime;
        }
      });

      return Object.values(groupMap).map(group => ({
        ...group,
        allMessages: group.allMessages.sort((a, b) => new Date(b.createDatetime) - new Date(a.createDatetime)),
        kpiGroups: Object.values(group.kpiGroups).map(kpiGroup => ({
          ...kpiGroup,
          messages: kpiGroup.messages.sort((a, b) => new Date(a.createDatetime) - new Date(b.createDatetime)),
        })).sort((a, b) => new Date(b.latestTime) - new Date(a.latestTime)),
      })).sort((a, b) => new Date(b.latestTime) - new Date(a.latestTime));
    },
    // Nhãn của option mặc định phải nói rõ đang rơi về đâu, không thì admin không biết
    // "(Theo mặc định)" nghĩa là coach nào.
    unassignedGroupCount() {
      if (!this.groupCustomerCounts) return 0;
      return this.allGroups.filter(g => !this.groupCustomerCounts[g.group_id]).length;
    },
    filteredAllGroups() {
      // Nhóm riêng (đúng 1 customer_receives) + nhóm chưa chỉ định khi bỏ tick; nhóm chung luôn ẩn.
      // null = chưa tải được thì hiện như cũ
      const counts = this.groupCustomerCounts;
      const base = counts
        ? this.allGroups.filter(g => counts[g.group_id] === 1 || (!this.hideUnassignedGroups && !counts[g.group_id]))
        : this.allGroups;
      if (!this.groupSearchQuery) return base;
      const q = this.groupSearchQuery.toLowerCase();
      return base.filter(g =>
        (g.name && g.name.toLowerCase().includes(q)) ||
        (g.group_id && g.group_id.toLowerCase().includes(q))
      );
    },
  },
  watch: {
    'paging.page'() {
      this.loadData();
    },
    activeTab(val) {
      if (val === 'message') {
        this.$nextTick(() => {
          const el = this.$refs.msgTabBody;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    },
    chatHistoryMessages(msgs) {
      if (!msgs.length) return;
      const scrollToBottom = () => {
        ['chatHistoryBody', 'mobileChatBody'].forEach(ref => {
          const el = this.$refs[ref];
          if (el) el.scrollTop = el.scrollHeight;
        });
      };
      this.$nextTick(scrollToBottom);
      setTimeout(scrollToBottom, 100);
    },
    selectedItem(item) {
      this.reviewReason = null;
      this.reviewNote = '';
      if (item) {
        this.loadChatHistory(item.accountId);
      } else {
        this.mobileChatOpen = false;
        this.chatHistoryMessages = [];
        this.chatHistoryGroupName = '';
        this.kpiView = null;
      }
    },
    mobileChatOpen(val) {
      if (val) {
        setTimeout(() => {
          ['chatHistoryBody', 'mobileChatBody'].forEach(ref => {
            const el = this.$refs[ref];
            if (el) el.scrollTop = el.scrollHeight;
          });
        }, 50);
      }
    },
  },
  async created() {
    this.initCoachFilter();
    // Danh sách + thống kê là thứ coach cần ngay; dữ liệu cấu hình nạp nền, không chặn
    this.loadSupportData();
    this.loadAllGroups();
    await Promise.all([this.loadData(), this.loadStats()]);

    const idFromUrl = this.$route.query.id;
    if (idFromUrl) {
      const found = this.data.find(i => String(i.id) === String(idFromUrl));
      if (found) await this.selectItem(found);
      else {
        // Item may not be on current page — fetch directly by id
        const freshItem = await this.fetchItem(idFromUrl);
        if (freshItem) await this.selectItem(freshItem);
      }
    }
    this.startHub();
  },
  beforeDestroy() {
    this.stopHub();
  },
  methods: {
    async startHub() {
      const baseUrl = process.env.VUE_APP_ZALO_WEBHOOK_URL.replace(/\/App$/, '');
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${baseUrl}/decision-ai-coach/hub`, {
          accessTokenFactory: async () => {
            return await new Mgr().getAcessToken();
          },
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.hubConnection.on('decision_new', (item) => {
        const normalized = this.normalizeItem(item);
        const exists = this.data.find(d => d.id === normalized.id);
        if (!exists) {
          this.data.unshift(normalized);
          this.stats.totalPending = (this.stats.totalPending || 0) + 1;
        }
      });

      this.hubConnection.on('zalo_message', (msg) => {
        const currentRecipient = this.selectedItem?.recipient;
        if (msg.recipient !== currentRecipient) return;

        // Skip if this is an OA message (already added via optimistic update)
        if (msg.senderType === 'oa') {
          const msgTime = Number(msg.timestamp);
          const isDuplicate = this.chatHistoryMessages.some(m => {
            if (m.senderType !== 'oa') return false;
            const timeDiff = Math.abs(Number(m.timestamp) - msgTime);
            if (timeDiff > 10000) return false; // 10 seconds window
            // Check by content match
            if (msg.imageUrl && m.imageUrl) return true; // Both are images within time window
            if (msg.message && m.message === msg.message) return true;
            return false;
          });
          if (isDuplicate) return;
        }

        this.chatHistoryMessages.push({
          id: msg.msgId || Date.now(),
          message: msg.message || '',
          imageUrl: msg.imageUrl || null,
          timestamp: Number(msg.timestamp),
          senderType: msg.senderType,
          userName: msg.userName || '',
        });
        this.$nextTick(() => {
          ['chatHistoryBody', 'mobileChatBody', 'msgTabBody'].forEach(ref => {
            const el = this.$refs[ref];
            if (el) el.scrollTop = el.scrollHeight;
          });
        });
      });

      this.hubConnection.on('decision_updated', (item) => {
        const normalized = this.normalizeItem(item);
        const idx = this.data.findIndex(d => d.id === normalized.id);
        if (idx !== -1) {
          this.$set(this.data, idx, normalized);
        }
        if (this.selectedItem && this.selectedItem.id === normalized.id) {
          // Update properties without replacing object (avoid triggering watcher/loadChatHistory)
          Object.assign(this.selectedItem, normalized);
        }
        this.loadStats();
      });

      this.hubConnection.onreconnected(async () => {
        if (this.canManageAll) {
          await this.hubConnection.invoke('JoinAdminGroup');
        } else {
          const coachId = this.currentUser?.id || this.currentUser?.Id;
          if (coachId) await this.hubConnection.invoke('JoinCoachGroup', coachId);
        }
      });

      try {
        await this.hubConnection.start();
        if (this.canManageAll) {
          await this.hubConnection.invoke('JoinAdminGroup');
        } else {
          const coachId = this.currentUser?.id || this.currentUser?.Id;
          if (coachId) await this.hubConnection.invoke('JoinCoachGroup', coachId);
        }
      } catch (e) {
        console.warn('SignalR connection failed, falling back to polling', e);
        this.startFallbackPolling();
      }
    },
    stopHub() {
      if (this.hubConnection) {
        this.hubConnection.stop();
        this.hubConnection = null;
      }
      if (this._pollInterval) {
        clearInterval(this._pollInterval);
      }
    },
    startFallbackPolling() {
      this._pollInterval = setInterval(() => {
        this.loadData();
        this.loadStats();
      }, 10000);
    },
    normalizeItem(item) {
      return {
        ...item,
        id: item.id || item.Id,
        createDatetime: item.createDatetime || item.CreateDatetime,
        sentAt: item.sentAt || item.SentAt,
        reviewedAt: item.reviewedAt || item.ReviewedAt,
        aiResponse: item.aiResponse || item.AiResponse,
        editedMessage: item.editedMessage || item.EditedMessage,
        kpiType: item.kpiType || item.KpiType,
        status: item.status ?? item.Status,
        patientName: item.patientName || item.PatientName,
        recipient: item.recipient || item.Recipient,
        patientId: item.patientId || item.PatientId,
        accountId: item.accountId || item.AccountId,
        code: item.code || item.Code || '',
        groupId: item.groupId || item.GroupId || '',
        isShared: item.isShared ?? item.IsShared ?? false,
        sharedContentAsk: item.sharedContentAsk || item.SharedContentAsk || '',
        originalMessage: item.originalMessage || item.OriginalMessage || '',
        sessionId: item.sessionId || item.SessionId || null,
      };
    },
    async handleRefresh() {
      this.$store.commit('context/setLoading', true);
      try {
        await Promise.all([
          this.loadData(),
          this.loadStats(),
          this.loadAIEnabledGroups()
        ]);
      } catch (error) {
        console.error('Refresh error:', error);
      } finally {
        this.$store.commit('context/setLoading', false);
      }
    },
    initCoachFilter() {
      this.currentUserId = this.currentUser?.id || this.currentUser?.Id;
      // Không có quyền quản lý: lọc theo chính mình
      if (!this.canManageAll) this.selectedCoachId = this.currentUserId;
    },
    // Nhóm bật AI và CustomerReceives chỉ gọi 1 lần lúc mở màn, dùng chung cho
    // danh sách coach (quản lý) và nhóm của chính coach
    async loadSupportData() {
      // Lỗi từng cái riêng như trước: một API hỏng không kéo theo cái kia
      const [enabledRes, crRes] = await Promise.all([
        decisionAiCoachService.getAIEnabledGroups().catch(error => {
          console.error('Failed to load AI enabled groups:', error);
          return null;
        }),
        decisionAiCoachService.getCustomerReceives().catch(error => {
          console.error('Failed to load customer receives mapping:', error);
          return null;
        }),
      ]);
      this.aiEnabledGroups = enabledRes?.data || enabledRes?.Data || [];
      this.customerReceivesData = crRes?.data?.data || crRes?.data || crRes?.Data || [];
      if (this.canManageAll) await this.loadCoaches();
    },
    async loadCoaches() {
      try {
        // Nhóm đã bật AI (bật AI = cần tham vấn, không còn danh sách duyệt riêng)
        const enabledGroups = this.aiEnabledGroups;
        if (enabledGroups.length === 0) {
          this.coaches = [];
          return;
        }

        // Coach gắn với nhóm đã bật AI, lấy từ CustomerReceives
        const crItems = this.customerReceivesData;
        const coachIdsInEnabledGroups = new Set();
        crItems.forEach(item => {
          const coachId = item.coachId || item.CoachId;
          const zaloGroupId = item.zaloGroupId || item.ZaloGroupId;
          if (coachId && zaloGroupId && enabledGroups.includes(zaloGroupId)) {
            coachIdsInEnabledGroups.add(coachId);
          }
        });

        // Chỉ giữ coach của khoá onboarding có gắn nhóm bật AI
        const ONBOARDING_ID = '350a3050-c0f7-11ef-b57a-03ea338ae610';
        const { data } = await decisionAiCoachService.getCoachesInCourse(ONBOARDING_ID);
        this.coaches = (data || [])
          .filter(c => coachIdsInEnabledGroups.has(c.id))
          .map(c => ({
            id: c.id,
            fullName: c.fullName
          }));
      } catch (error) {
        console.error('Error loading coaches:', error);
        this.coaches = [];
      }
    },
    onCoachSelect(option) {
      this.selectedCoachId = option || null;
      this.paging.page = 1;
      this.loadData();
      this.loadStats();
    },
    // LoadingButton cần Promise để tự hiện / tắt spinner
    loadMore() {
      if (this.loadingMore) return Promise.resolve();
      this.paging.page += 1;
      return this.loadData(true);
    },
    // append = true: nối trang tiếp vào danh sách đang có (nút Tải thêm)
    async loadData(append = false) {
      if (append) this.loadingMore = true;
      else this.loading = true;
      try {
        // Phân trang theo nhóm: 20 nhóm / lần, mỗi nhóm kèm đủ tin theo trạng thái / chủ đề đang lọc
        const params = {
          page: this.paging.page,
          size: this.paging.pageSize,
          groupPaging: true,
        };
        if (this.filter.status !== null) params.status = this.filter.status;
        if (this.filter.kpiType) params.kpiType = this.filter.kpiType;
        if (this.filter.isShared === true) {
          params.isShared = true;
          params.status = 0; // Chia sẻ chỉ hiện items chờ duyệt
        }
        if (this.searchQuery) params.search = this.searchQuery;
        if (this.sort.by) params.orderBy = `${this.sort.by} ${this.sort.order}`;

        // Không có quyền quản lý thì luôn lọc theo coachId của chính mình
        const myCoachId = this.currentUser?.id || this.currentUser?.Id;
        if (!this.canManageAll && myCoachId) {
          params.coachId = myCoachId;
        } else if (this.selectedCoachId) {
          params.coachId = this.selectedCoachId;
        }

        const result = await decisionAiCoachService.getList(params);
        let items = result.items || result.Items || result.data?.items || result.Data?.Items || [];
        // Normalize field names (handle both camelCase and PascalCase)
        const mapped = items.map(item => ({
          ...item,
          id: item.id || item.Id,
          createDatetime: item.createDatetime || item.CreateDatetime,
          sentAt: item.sentAt || item.SentAt,
          reviewedAt: item.reviewedAt || item.ReviewedAt,
          aiResponse: item.aiResponse || item.AiResponse,
          editedMessage: item.editedMessage || item.EditedMessage,
          kpiType: item.kpiType || item.KpiType,
          status: item.status ?? item.Status,
          patientName: item.patientName || item.PatientName,
          recipient: item.recipient || item.Recipient,
          patientId: item.patientId || item.PatientId,
          accountId: item.accountId || item.AccountId,
          code: item.code || item.Code || '',
          groupId: item.groupId || item.GroupId || '',
          isShared: item.isShared ?? item.IsShared ?? false,
          sharedContentAsk: item.sharedContentAsk || item.SharedContentAsk || '',
          originalMessage: item.originalMessage || item.OriginalMessage || '',
          sessionId: item.sessionId || item.SessionId || null,
        }));
        if (append) {
          // Có tin mới chen vào thì trang sau lặp lại vài tin cũ, bỏ trùng theo id
          const ids = new Set(this.data.map(x => x.id));
          this.data = [...this.data, ...mapped.filter(x => !ids.has(x.id))];
        } else {
          this.data = mapped;
        }
        this.paging.total = result.total || result.Total || result.data?.total || 0;
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải dữ liệu' });
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    async loadStats() {
      try {
        const params = {};
        if (this.filter.kpiType) {
          params.kpiType = this.filter.kpiType;
        }

        // Không có quyền quản lý thì luôn lọc theo coachId của chính mình
        const myCoachId = this.currentUser?.id || this.currentUser?.Id;
        if (!this.canManageAll && myCoachId) {
          params.coachId = myCoachId;
        } else if (this.selectedCoachId) {
          params.coachId = this.selectedCoachId;
        }
        const data = await decisionAiCoachService.getStats(params);
        if (data) {
          this.stats = {
            totalPending: data.totalPending ?? data.TotalPending ?? 0,
            totalApproved: data.totalApproved ?? data.TotalApproved ?? 0,
            totalEdited: data.totalEdited ?? data.TotalEdited ?? 0,
            totalRejected: data.totalRejected ?? data.TotalRejected ?? 0,
            totalSent: data.totalSent ?? data.TotalSent ?? 0,
            totalSkipped: data.totalSkipped ?? data.TotalSkipped ?? 0,
            totalToday: data.totalToday ?? data.TotalToday ?? 0,
            totalShared: data.totalShared ?? data.TotalShared ?? 0,
          };
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
      }
    },
    async loadAIEnabledGroups() {
      try {
        const data = await decisionAiCoachService.getAIEnabledGroups();
        this.aiEnabledGroups = data.data || data.Data || [];
        // Cần mapping nhóm -> coach để lọc nhóm của chính mình
        if (!this.canManageAll) {
          await this.loadCustomerReceivesMapping();
        }
      } catch (error) {
        console.error('Failed to load AI enabled groups:', error);
        this.aiEnabledGroups = [];
      }
    },
    async loadCustomerReceivesMapping() {
      try {
        const response = await decisionAiCoachService.getCustomerReceives();
        this.customerReceivesData = response?.data?.data || response?.data || response?.Data || [];
      } catch (error) {
        console.error('Failed to load customer receives mapping:', error);
        this.customerReceivesData = [];
      }
    },
    async loadStyleCoaches() {
      try {
        // webhook.get trả thẳng body, không phải axios response
        const body = (await decisionAiCoachService.getStyleCoachesByTopic()) || {};
        if (body.success === false) {
          console.error('[loadStyleCoaches]', body.error);
          this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được danh sách văn phong coach' });
          return;
        }
        const data = body.data || {};
        this.styleTopics = data.topics || [];
        this.styleMinSamples = data.minSamples || 1;
        this.styleMaxPairs = data.maxPairs || 5;
        // Chuẩn hoá về { coachId, pairIds } bất kể BE trả PascalCase hay camelCase
        const styles = {};
        Object.entries(data.groupStyles || {}).forEach(([groupId, topics]) => {
          styles[groupId] = {};
          Object.entries(topics || {}).forEach(([kpiType, s]) => {
            const coachId = s && (s.coachId || s.CoachId);
            if (coachId) styles[groupId][kpiType] = { coachId, pairIds: (s.pairIds || s.PairIds || []).slice() };
          });
        });
        this.groupTopicStyles = styles;
      } catch (error) {
        console.error('Failed to load style coaches:', error);
        this.styleTopics = [];
      }
    },
    customStyleCount(groupId) {
      return Object.keys(this.styleDraft[groupId] || {}).length;
    },
    async openStyleModal(group) {
      this.styleGroupSearch = '';
      this.showStyleModal = true;
      // Mở lần nào tải lại lần đó: coach vừa duyệt thêm mẫu thì phải hiện ngay, không chờ F5
      this.styleSamples = {};
      await this.loadStyleCoaches();
      this.styleDraft = this.cloneStyles(this.groupTopicStyles);
      const first = this.styleGroupsFiltered[0];
      this.styleGroupId = group ? group.group_id : (first ? first.group_id : null);
      this.expandedSampleTopic = null;
    },
    pickTopicStyle(kpiType, coachId) {
      if (!this.styleGroupId || (this.topicStyleOf(this.styleGroupId, kpiType).coachId || '') === coachId) return;
      // Đổi coach thì bỏ câu ghim cũ - câu đó thuộc coach khác
      this.setDraftStyle(this.styleGroupId, kpiType, coachId, []);
      if (this.expandedSampleTopic === kpiType && coachId) this.loadStyleSamples(coachId, kpiType);
    },
    topicStyleOf(groupId, kpiType) {
      return (this.styleDraft[groupId] || {})[kpiType] || { coachId: '', pairIds: [] };
    },
    samplesOf(kpiType) {
      const coachId = this.topicStyleOf(this.styleGroupId, kpiType).coachId;
      return this.styleSamples[`${coachId}|${kpiType}`] || { loading: true, items: [] };
    },
    isPairPinned(kpiType, id) {
      return this.topicStyleOf(this.styleGroupId, kpiType).pairIds.includes(id);
    },
    toggleStyleSamples(kpiType) {
      if (this.expandedSampleTopic === kpiType) {
        this.expandedSampleTopic = null;
        return;
      }
      this.expandedSampleTopic = kpiType;
      this.loadStyleSamples(this.topicStyleOf(this.styleGroupId, kpiType).coachId, kpiType);
    },
    async loadStyleSamples(coachId, kpiType) {
      const key = `${coachId}|${kpiType}`;
      if (!coachId || (this.styleSamples[key] && !this.styleSamples[key].loading)) return;
      this.$set(this.styleSamples, key, { loading: true, items: [] });
      try {
        const body = (await decisionAiCoachService.getStyleSamples(coachId, kpiType)) || {};
        if (body.success === false) throw new Error(body.error);
        this.$set(this.styleSamples, key, { loading: false, items: body.data || [] });
      } catch (error) {
        console.error('Failed to load style samples:', error);
        this.$delete(this.styleSamples, key);
        this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được câu mẫu' });
      }
    },
    togglePinnedPair(kpiType, id) {
      const current = this.topicStyleOf(this.styleGroupId, kpiType).pairIds;
      const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
      if (next.length > this.styleMaxPairs) return;
      this.setTopicPairs(kpiType, next);
    },
    setTopicPairs(kpiType, pairIds) {
      const coachId = this.topicStyleOf(this.styleGroupId, kpiType).coachId;
      if (!coachId) return;
      this.setDraftStyle(this.styleGroupId, kpiType, coachId, pairIds);
    },
    // Chỉ sửa bản nháp, bấm Lưu mới gửi BE. Vue 2 cần $set cho key mới
    setDraftStyle(groupId, kpiType, coachId, pairIds = []) {
      const map = { ...(this.styleDraft[groupId] || {}) };
      if (coachId) map[kpiType] = { coachId, pairIds };
      else delete map[kpiType];
      this.$set(this.styleDraft, groupId, map);
    },
    cloneStyles(src) {
      return JSON.parse(JSON.stringify(src || {}));
    },
    onStyleModalHide(e) {
      if (this.savingStyles) {
        e.preventDefault();
        return;
      }
      if (this.styleChanges.length && !window.confirm('Còn thay đổi chưa lưu. Đóng và bỏ các thay đổi này?')) {
        e.preventDefault();
        return;
      }
      this.styleDraft = this.cloneStyles(this.groupTopicStyles);
    },
    async saveStyleChanges() {
      const changes = this.styleChanges;
      if (!changes.length) return;
      this.savingStyles = true;
      let failed = 0;
      for (const c of changes) {
        try {
          const res = await decisionAiCoachService.setGroupTopicStyleCoach(c.groupId, c.kpiType, c.coachId, c.pairIds);
          if (res?.success === false) throw new Error(res.error || 'Lưu thất bại');
          const map = { ...(this.groupTopicStyles[c.groupId] || {}) };
          if (c.coachId) map[c.kpiType] = { coachId: c.coachId, pairIds: [...c.pairIds] };
          else delete map[c.kpiType];
          this.$set(this.groupTopicStyles, c.groupId, map);
        } catch (error) {
          failed++;
          console.error('Failed to set topic style coach:', c, error);
        }
      }
      this.savingStyles = false;
      if (failed) this.$toastr.e({ title: 'Lỗi', msg: `Không lưu được ${failed}/${changes.length} thay đổi` });
      else this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu văn phong' });
    },
    async loadGroupCustomerCounts() {
      try {
        const body = (await decisionAiCoachService.getGroupCustomerCounts()) || {};
        if (body.success === false) throw new Error(body.error);
        this.groupCustomerCounts = body.data || {};
      } catch (error) {
        console.error('Failed to load group customer counts:', error);
        this.groupCustomerCounts = null;
      }
    },

    async loadAllGroups() {
      this.loadingGroups = true;
      try {
        // First call to get total
        const firstRes = await decisionAiCoachService.getValidGroupOA({ offset: 0, count: 50 });
        const { total, groups: firstGroups } = firstRes.data;

        // Các trang còn lại gọi song song, không chờ nối đuôi từng trang
        const restPages = Math.max(Math.ceil(total / 50) - 1, 0);
        const rest = await Promise.all(
          Array.from({ length: restPages }, (_, i) =>
            decisionAiCoachService.getValidGroupOA({ offset: (i + 1) * 50, count: 50 }),
          ),
        );
        this.allGroups = [
          ...(firstGroups || []),
          ...rest.flatMap(res => res?.data?.groups || []),
        ];
      } catch (error) {
        console.error('Failed to load all groups:', error);
        this.allGroups = [];
      } finally {
        this.loadingGroups = false;
      }
    },
    makeDraggable(modalId) {
      const modal = document.getElementById(modalId);
      if (!modal) return;
      const dialog = modal.querySelector('.modal-dialog');
      const header = modal.querySelector('.modal-header');
      if (!dialog || !header || header._draggable) return;
      header._draggable = true;
      const rect = dialog.getBoundingClientRect();
      dialog.style.margin = '0';
      dialog.style.position = 'absolute';
      dialog.style.left = rect.left + 'px';
      dialog.style.top = rect.top + 'px';
      header.style.cursor = 'move';
      let startX, startY, initLeft, initTop;
      const onMouseMove = (e) => {
        dialog.style.left = initLeft + (e.clientX - startX) + 'px';
        dialog.style.top = initTop + (e.clientY - startY) + 'px';
      };
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        header.style.cursor = 'move';
      };
      header.addEventListener('mousedown', (e) => {
        initLeft = parseFloat(dialog.style.left);
        initTop = parseFloat(dialog.style.top);
        startX = e.clientX;
        startY = e.clientY;
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });
    },
    openShareModal() {
      this.selectedShareCoachIds = [];
      this.contentAsk = this.selectedItem?.sharedContentAsk || '';
      this.showShareModal = true;
    },
    async loadCoachesForShare() {
      this.loadingCoaches = true;
      try {
        const ONBOARDING_ID = '350a3050-c0f7-11ef-b57a-03ea338ae610';
        const res = await decisionAiCoachService.getCoachesInCourse(ONBOARDING_ID);
        this.availableCoaches = res?.data || [];
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được danh sách coach' });
      } finally {
        this.loadingCoaches = false;
      }
    },
    isCoachSelected(id) {
      return this.selectedShareCoachIds.includes(id);
    },
    toggleCoachSelection(id) {
      const idx = this.selectedShareCoachIds.indexOf(id);
      if (idx === -1) this.selectedShareCoachIds.push(id);
      else this.selectedShareCoachIds.splice(idx, 1);
    },
    async handleShare(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.selectedItem || this.selectedShareCoachIds.length === 0) return;
      this.sharing = true;
      try {
        // Gửi nội dung hỏi từ textarea (contentAsk)
        const content = this.contentAsk || '';
        await decisionAiCoachService.shareItem(this.selectedItem.id, this.selectedShareCoachIds, content);
        this.$toastr.s({ title: 'Thành công', msg: 'Chia sẻ thành công!' });
        this.showShareModal = false;
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Chia sẻ thất bại, vui lòng thử lại' });
      } finally {
        this.sharing = false;
      }
    },
    openMediaPreview(url, type = 'image') {
      const scrollY = window.scrollY;
      this.previewMedia = { url, type };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    },
    closeMediaPreview() {
      const scrollY = document.body.style.top;
      this.previewMedia = null;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    },
    onImageError(msg) {
      if (msg.fallbackUrl && msg.imageUrl !== msg.fallbackUrl) {
        msg.imageUrl = msg.fallbackUrl;
        return;
      }
      // Hết đường dự phòng (link Zalo hết hạn, cronjob chưa kịp backup) - ẩn ảnh vỡ như màn reminder
      this.$set(msg, 'imageBroken', true);
    },
    getFileName(url) {
      if (!url) return 'File';
      return url.split('/').pop() || 'File';
    },
    getFileIcon(url) {
      const fileName = this.getFileName(url).toLowerCase();
      if (fileName.endsWith('.pdf')) return '📕';
      if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return '📄';
      if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return '📊';
      if (fileName.endsWith('.ppt') || fileName.endsWith('.pptx')) return '📈';
      if (fileName.endsWith('.zip') || fileName.endsWith('.rar')) return '📦';
      if (fileName.endsWith('.txt')) return '📝';
      return '📎';
    },
    // Chon file chi giu lai o composer, cho coach nhap chu roi bam gui
    handleAttachment(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file || !this.selectedItem) return;

      this.clearPendingFile();
      this.pendingFile = file;
      if (file.type.startsWith('image/')) {
        this.pendingPreviewUrl = URL.createObjectURL(file);
      }
    },
    clearPendingFile() {
      if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
      this.pendingFile = null;
      this.pendingPreviewUrl = null;
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        ['chatHistoryBody', 'mobileChatBody'].forEach(ref => {
          const el = this.$refs[ref];
          if (el) el.scrollTop = el.scrollHeight;
        });
      });
    },
    async sendDirectMessage() {
      if (!this.selectedItem) return;

      const sentText = this.directMessage;
      const file = this.pendingFile;
      if (!sentText.trim() && !file) return;

      this.sendingDirect = true;
      this.directMessage = '';
      try {
        if (file) {
          await this.sendAttachmentWithText(file, sentText.trim());
        } else {
          await this.sendTextOnly(sentText);
        }
      } finally {
        this.sendingDirect = false;
      }
    },
    async sendTextOnly(sentText) {
      // Optimistic update
      this.chatHistoryMessages.push({
        id: Date.now(),
        message: sentText,
        imageUrl: null,
        timestamp: Date.now(),
        senderType: 'oa',
        userName: '',
      });
      this.scrollChatToBottom();
      try {
        await decisionAiCoachService.sendDirectMessage(this.selectedItem.id, sentText);
      } catch (e) {
        // Rollback optimistic message on failure
        this.chatHistoryMessages.pop();
        this.directMessage = sentText;
        this.$toastr.e({ title: 'Lỗi', msg: 'Gửi tin nhắn thất bại' });
      }
    },
    // File va chu di chung mot request -> BE gop thanh mot tin nhan Zalo
    async sendAttachmentWithText(file, caption) {
      const isImage = file.type.startsWith('image/');
      const mediaType = isImage ? 'image' : 'file';
      const previewUrl = this.pendingPreviewUrl;

      // Nha cho o composer ngay, nhung giu previewUrl cho bubble optimistic dung tam
      this.pendingFile = null;
      this.pendingPreviewUrl = null;

      const tempMsg = {
        id: Date.now(),
        message: caption || null,
        imageUrl: isImage ? previewUrl : null,
        fileUrl: null,
        fileName: isImage ? null : file.name,
        timestamp: Date.now(),
        senderType: 'oa',
        userName: '',
        uploading: true,
      };
      this.chatHistoryMessages.push(tempMsg);
      this.scrollChatToBottom();

      this.uploadingMedia = true;
      try {
        const mediaUrl = await decisionAiCoachService.uploadMedia(file);
        if (!mediaUrl || typeof mediaUrl !== 'string') throw new Error('Upload failed');
        await decisionAiCoachService.sendMedia(this.selectedItem.id, mediaUrl, mediaType, caption);
        // Replace optimistic with real
        const idx = this.chatHistoryMessages.findIndex(m => m.id === tempMsg.id);
        if (idx !== -1) {
          this.$set(this.chatHistoryMessages, idx, {
            ...tempMsg,
            imageUrl: isImage ? mediaUrl : null,
            fileUrl: isImage ? null : mediaUrl,
            fileName: isImage ? null : file.name,
            uploading: false,
          });
        }
      } catch (e) {
        this.chatHistoryMessages = this.chatHistoryMessages.filter(m => m.id !== tempMsg.id);
        // Tra lai ca chu lan file vao composer de coach bam gui lai, khong phai chon file lai
        this.directMessage = caption;
        this.pendingFile = file;
        this.pendingPreviewUrl = isImage ? URL.createObjectURL(file) : null;
        this.$toastr.e({ title: 'Lỗi', msg: 'Gửi file thất bại' });
      } finally {
        this.uploadingMedia = false;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
      }
    },
    async openGroupConfig() {
      // Chặn cả ở hành động, không chỉ ẩn nút
      if (!this.canManageAll) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Bạn không có quyền cấu hình nhóm' });
        return;
      }
      this.showGroupConfigModal = true;
      this.groupSearchQuery = '';

      // Nạp lại từ server trước khi copy. Trước đây chỉ copy state trong bộ nhớ nạp từ lúc
      // mounted - lần nạp đó lỗi hoặc chưa xong thì modal mở ra trắng, trông như mất cấu hình.
      await this.loadAIEnabledGroups();

      this.selectedAIGroupIds = [...this.aiEnabledGroups];
      this.pendingAiTargets = {};

      // Nhóm chung bị ẩn khỏi danh sách nhưng vẫn giữ trong selectedAIGroupIds -> cấu hình cũ không đổi
      await Promise.all([this.loadAllGroups(), this.loadGroupCustomerCounts()]);
      this.loadAiCandidateCounts();

      // Load lúc mở modal chứ không phải lúc mount: số lượt của mỗi coach đổi theo ngày,
      // và dropdown này chỉ xuất hiện trong modal. Đang ẩn thì khỏi gọi API cho phí.
      if (this.showStyleCoachPicker)
        await this.loadStyleCoaches();
    },
    isAIGroupSelected(group) {
      return this.selectedAIGroupIds.includes(group.group_id);
    },
    toggleAIGroupSelection(group) {
      const index = this.selectedAIGroupIds.indexOf(group.group_id);
      if (index === -1) {
        this.selectedAIGroupIds.push(group.group_id);
        // Chỉ nhóm vừa bật mới tự hỏi; nhóm đã bật từ trước giữ nguyên
        if (!this.aiEnabledGroups.includes(group.group_id)) this.openAiTargetPicker(group, true);
      } else {
        this.selectedAIGroupIds.splice(index, 1);
      }
    },
    async loadAiCandidateCounts() {
      try {
        // Mỗi nhóm = 1 lượt gọi Zalo listmember qua proxy -> chỉ đếm nhóm đang bật AI (nút 👤 chỉ hiện ở đó)
        const body = (await decisionAiCoachService.getGroupAiCandidateCounts(this.selectedAIGroupIds)) || {};
        if (body.success === false) throw new Error(body.error);
        this.aiCandidateCounts = body.data || {};
      } catch (error) {
        console.error('Failed to load AI candidate counts:', error);
        this.aiCandidateCounts = {};
      }
    },
    aiTargetLabel(groupId) {
      if (groupId in this.pendingAiTargets) {
        return this.pendingAiTargets[groupId] ? (this.aiTargetNames[groupId] || 'Đã chọn') : 'Tất cả';
      }
      return this.aiTargetNames[groupId] || 'Khách hàng';
    },
    async openAiTargetPicker(group, isNew) {
      this.aiTarget = { show: false, group, candidates: [], loading: true, choice: '', isNew };
      if (!isNew) this.aiTarget.show = true;
      try {
        const body = (await decisionAiCoachService.getGroupAiCandidates(group.group_id)) || {};
        if (body.success === false) throw new Error(body.error);
        const candidates = (body.data || []).map(c => ({
          zaloUserId: c.zaloUserId ?? c.ZaloUserId,
          name: c.name ?? c.Name,
          avatar: c.avatar ?? c.Avatar,
          messageCount: c.messageCount ?? c.MessageCount ?? 0,
          lastMessage: c.lastMessage ?? c.LastMessage,
          isSelected: c.isSelected ?? c.IsSelected,
        }));
        const selected = candidates.find(c => c.isSelected);
        const pending = this.pendingAiTargets[group.group_id];
        this.aiTarget.candidates = candidates;
        this.$set(this.aiCandidateCounts, group.group_id, candidates.length);
        this.aiTarget.choice = pending !== undefined ? pending : (selected ? selected.zaloUserId : '');
        if (selected && !(group.group_id in this.aiTargetNames)) {
          this.$set(this.aiTargetNames, group.group_id, selected.name || selected.zaloUserId);
        }
        // Vừa bật: chỉ hỏi khi nhóm có từ 2 người (không tính coach/OA)
        if (isNew && candidates.length >= 2) this.aiTarget.show = true;
      } catch (error) {
        console.error('Failed to load AI candidates:', error);
        if (!isNew) this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được danh sách người trong nhóm' });
      } finally {
        this.aiTarget.loading = false;
      }
    },
    confirmAiTarget() {
      const { group, choice, candidates } = this.aiTarget;
      if (!group) return;
      this.$set(this.pendingAiTargets, group.group_id, choice || '');
      const picked = candidates.find(c => c.zaloUserId === choice);
      this.$set(this.aiTargetNames, group.group_id, picked ? (picked.name || picked.zaloUserId) : '');
    },
    cancelAiTarget() {
      // Huỷ lúc vừa tick bật -> bỏ tick, nhóm chưa được bật
      const { group, isNew } = this.aiTarget;
      if (isNew && group) {
        const i = this.selectedAIGroupIds.indexOf(group.group_id);
        if (i !== -1) this.selectedAIGroupIds.splice(i, 1);
      }
    },
    async saveGroupConfig(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.canManageAll) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Bạn không có quyền cấu hình nhóm' });
        return;
      }
      this.$store.commit('context/setLoading', true);
      try {
        // Chỉ còn một danh sách: bật AI đồng nghĩa với cần tham vấn.
        // Nhóm chạy tự động cấu hình thẳng trong DB (AI_AUTO_ENABLED_GROUPS) - BE suy ra
        // "cần duyệt = bật AI và không có trong auto", màn này không cần biết.
        const aiResult = await decisionAiCoachService.saveAIEnabledGroups(this.selectedAIGroupIds);

        if (aiResult.success) {
          this.aiEnabledGroups = [...this.selectedAIGroupIds];

          const targets = Object.entries(this.pendingAiTargets)
            .filter(([groupId]) => this.selectedAIGroupIds.includes(groupId));
          for (const [groupId, zaloUserId] of targets) {
            await decisionAiCoachService.setGroupAiTarget(groupId, zaloUserId);
          }
          this.pendingAiTargets = {};

          // BE đã đọc lại DB: chỉ báo thành công khi mọi nhóm bật AI đều có thread agent
          const failed = (aiResult.threads || []).filter(t => !(t.ok ?? t.Ok));
          if (!failed.length) {
            this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu cấu hình nhóm và khởi tạo AI cho các nhóm!' });
            this.showGroupConfigModal = false;
          } else {
            const names = failed.map(t => {
              const id = t.groupId ?? t.GroupId;
              const g = this.allGroups.find(x => x.group_id === id);
              return `${(g && g.name) || id} (${t.error ?? t.Error})`;
            });
            this.$toastr.w({
              title: 'Đã lưu, nhưng chưa khởi tạo xong AI',
              msg: `${names.join('; ')}. Bấm Lưu lại để thử tạo lại.`,
            });
          }
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: 'Không thể lưu cấu hình' });
        }
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể lưu cấu hình' });
      } finally {
        this.$store.commit('context/setLoading', false);
      }
    },
    setStatusFilter(status) {
      this.filter.status = status;
      this.filter.isShared = null;
      this.paging.page = 1;
      this.loadData();
    },
    setSharedFilter(isShared) {
      this.filter.isShared = isShared;
      this.filter.status = null;
      this.paging.page = 1;
      this.loadData();
    },
    setKpiFilter(kpiType) {
      this.filter.kpiType = kpiType;
      this.paging.page = 1;
      this.loadData();
      this.loadStats();
    },
    resetFilters() {
      this.filter = { status: null, kpiType: null };
      this.searchQuery = '';
      this.paging.page = 1;
      this.loadData();
    },
    toggleGroup(group) {
      this.$set(this.expandedGroups, group.groupKey, !group.expanded);
    },
    toggleKpi(groupKey, kpi) {
      const key = `${groupKey}_${kpi.kpiType}`;
      this.$set(this.expandedKpis, key, !kpi.expanded);
    },
    toggleDate(groupKey, kpiType, dateKey, expanded) {
      const key = `${groupKey}_${kpiType}_${dateKey}`;
      this.$set(this.expandedDates, key, !expanded);
    },
    formatTime(dateVal) {
      const date = this.parseUtcDate(dateVal);
      if (!date) return '-';
      return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    },
    formatSessionTime(dateVal) {
      const date = this.parseUtcDate(dateVal);
      if (!date) return '-';
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      if (isToday) {
        return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
      }
      return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) + ' ' +
             date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    },
    getKpiDotColor(kpiType) {
      return kpiMeta(kpiType).dot;
    },
    getStatusDotColor(status) {
      const colors = { 0: '#BA7517', 1: '#378ADD', 2: '#E24B4A', 3: '#1D9E75' };
      return colors[status] || '#888780';
    },
    onSearchDebounce() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.paging.page = 1;
        this.loadData();
      }, 300);
    },
    async selectItem(item) {
      if (!item.reviewedAt) {
        await this.markAsViewed(item.id);
      }

      const freshItem = await this.fetchItem(item.id);
      if (freshItem) {
        this.selectedItem = freshItem;
        this.editMessage = freshItem.editedMessage || freshItem.aiResponse || '';
      } else {
        this.selectedItem = item;
        this.editMessage = item.editedMessage || item.aiResponse || '';
      }
      this.coachNote = '';
      this.statsExpanded = false; // mỗi tin có chỉ số mở ra đều bắt đầu với Thống kê thu gọn
      this.activeTab = 'review';
      this.directMessage = '';
      this.clearPendingFile();
      this.loadApprovalStats(this.selectedItem.kpiType || this.selectedItem.KpiType);
      this.loadKpi(this.selectedItem);

      if (this.$route.query.id !== String(item.id)) {
        this.$router.replace({ query: { ...this.$route.query, id: item.id } });
      }
    },
    async loadKpi(item) {
      this.kpiView = null;
      if (!item || !item.hasKpiPayload) return;

      const id = item.id;
      try {
        const body = (await decisionAiCoachService.getKpi(id)) || {};
        // Coach đã bấm sang tin khác trong lúc chờ thì bỏ kết quả cũ
        if (!this.selectedItem || this.selectedItem.id !== id) return;
        this.kpiView = body.data || null;
      } catch (e) {
        console.error('[loadKpi]', e);
      }
    },
    // KpiEditor báo đã lưu (1) / bỏ qua (2) / xoá (null) - đồng bộ danh sách, panel và modal cùng lúc
    // nutrition: bản coach đã sửa khi lưu -> thay bản AI để cột Calo / món hiện đúng cái đã lưu
    onKpiHandled({ id, status, nutrition }) {
      const now = new Date().toISOString();
      const saved = nutrition ? { nutrition } : {};

      const row = this.data.find(x => x.id === id);
      if (row) {
        this.$set(row, 'kpiStatus', status);
        if (status === null) this.$set(row, 'hasKpiPayload', false);
      }

      if (this.selectedItem && this.selectedItem.id === id) {
        this.$set(this.selectedItem, 'kpiStatus', status);
        if (status === null) this.$set(this.selectedItem, 'hasKpiPayload', false);
      }

      if (this.kpiView && this.kpiView.id === id) {
        this.kpiView = status === null ? null : { ...this.kpiView, ...saved, kpiStatus: status, kpiSavedAt: now };
      }

      const idx = this.overviewItems.findIndex(x => x.id === id);
      if (idx >= 0) {
        // Cột khách hàng: bớt 1 chờ lưu khi tin rời trạng thái chờ; xoá thì bớt luôn tổng.
        // Không gỡ người đó khỏi danh sách ngay, coach đang thao tác dở trên họ
        const pt = this.overviewPatients.find(x => this.patientKey(x) === this.overviewPatientKey);
        if (pt) {
          if (this.overviewItems[idx].kpiStatus === 0 && status !== 0) pt.pendingCount = Math.max(0, pt.pendingCount - 1);
          if (status === null) pt.totalCount = Math.max(0, pt.totalCount - 1);
        }

        if (status === null) {
          this.overviewItems.splice(idx, 1);
          if (this.overviewExpandedId === id) this.overviewExpandedId = null;
        } else {
          this.$set(this.overviewItems, idx, { ...this.overviewItems[idx], ...saved, kpiStatus: status, kpiSavedAt: now });
        }
      }
    },
    // image_url của tin duyệt: 1 link hoặc mảng JSON khi khách gửi nhiều ảnh cùng lúc
    decisionImages(value) {
      if (!value) return [];
      let urls = [value];
      if (typeof value === 'string' && value.trim().startsWith('[')) {
        try {
          urls = JSON.parse(value);
        } catch (e) {
          urls = [];
        }
      }
      return (Array.isArray(urls) ? urls : []).filter(u => this.isShowableImage(u));
    },
    isShowableImage(url) {
      return !!url && /^https?:\/\//i.test(url) && !this.badImages[url];
    },
    markBadImage(url) {
      if (url) this.$set(this.badImages, url, true);
    },
    patientKey(pt) {
      return `${pt.patientId}|${pt.accountId}`;
    },
    // Cùng quy tắc giới hạn coach với danh sách tin (loadData)
    listCoachId() {
      const myCoachId = this.currentUser?.id || this.currentUser?.Id;
      if (!this.canManageAll && myCoachId) return myCoachId;
      return this.selectedCoachId || undefined;
    },
    async openKpiOverview() {
      this.overviewStatus = 'all';
      this.overviewExpandedId = null;
      this.overviewPatientSearch = '';
      this.showKpiOverview = true;
      await this.loadKpiPatients();

      // Đang mở 1 tin thì chọn sẵn khách hàng của tin đó (kể cả khi họ không còn gì chờ lưu)
      const item = this.selectedItem;
      if (item && item.patientId && item.accountId) {
        const found = this.overviewPatients.find(pt => pt.patientId === item.patientId && pt.accountId === item.accountId);
        this.selectOverviewPatient(found || {
          anchorId: item.id,
          patientId: item.patientId,
          accountId: item.accountId,
          patientName: item.patientName,
        }, item.kpiType === 'nutrition' ? 'nutrition' : null);
        return;
      }

      if (this.overviewPatients.length) this.selectOverviewPatient(this.overviewPatients[0]);
      else {
        this.overviewAnchorId = null;
        this.overviewPatientKey = null;
        this.overviewItems = [];
      }
    },
    async loadKpiPatients() {
      this.overviewPatientsLoading = true;
      try {
        const body = (await decisionAiCoachService.getKpiPatients({
          coachId: this.listCoachId(),
          days: this.overviewDays,
          pendingOnly: this.overviewPendingOnly,
        })) || {};
        this.overviewPatients = body.data || [];
      } catch (e) {
        console.error('[loadKpiPatients]', e);
        this.overviewPatients = [];
        this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được danh sách khách hàng' });
      } finally {
        this.overviewPatientsLoading = false;
      }
    },
    selectOverviewPatient(pt, preferTab = null) {
      this.overviewPatientKey = this.patientKey(pt);
      this.overviewAnchorId = pt.anchorId;
      this.overviewPatientName = pt.patientName || '';
      this.overviewStatus = 'all';
      if (preferTab) this.overviewTab = preferTab;
      this.loadKpiOverview();
    },
    onOverviewDaysChange() {
      this.loadKpiPatients();
      this.loadKpiOverview();
    },
    async loadKpiOverview() {
      if (!this.overviewAnchorId) return;
      const anchorId = this.overviewAnchorId;
      this.overviewLoading = true;
      this.overviewExpandedId = null;
      try {
        const body = (await decisionAiCoachService.getPatientKpiOverview(anchorId, this.overviewDays)) || {};
        if (this.overviewAnchorId !== anchorId) return;
        this.overviewItems = body.data || [];
        // Tab đang chọn trống mà tab kia có dữ liệu thì tự chuyển, khỏi bắt coach đoán
        if (!this.overviewInTab.length) {
          const other = this.overviewTabs.find(t => this.overviewCountByTab(t.key) > 0);
          if (other) this.overviewTab = other.key;
        }
      } catch (e) {
        console.error('[loadKpiOverview]', e);
        this.overviewItems = [];
        this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được chỉ số' });
      } finally {
        this.overviewLoading = false;
      }
    },
    // Payload đường huyết không chắc có kpi_type chuẩn, cứ có glucoseRows là tab đường huyết
    overviewTabOf(v) {
      return v.nutrition ? 'nutrition' : 'blood_sugar';
    },
    overviewCountByTab(tab) {
      return this.overviewItems.filter(v => this.overviewTabOf(v) === tab).length;
    },
    overviewCountByStatus(status) {
      return this.overviewInTab.filter(v => v.kpiStatus === status).length;
    },
    glucoseTfLabel(id) {
      return (GLUCOSE_TIME_FRAMES.find(t => t.id === id) || {}).label || '—';
    },
    mealTfLabel(id) {
      return (MEAL_TIME_FRAMES.find(t => t.id === id) || {}).label || '—';
    },
    mealItems(v) {
      return this.kpiDrafts[v.id] || (v.nutrition && v.nutrition.items) || [];
    },
    // Coach lưu thay đổi ở modal: thay bản trên dòng, về chế độ xem - nút Lưu trên dòng sẽ lưu đúng bản này sang app
    onKpiUpdated({ id, nutrition, glucoseRows }) {
      const changes = nutrition ? { nutrition } : { glucoseRows };
      const idx = this.overviewItems.findIndex(x => x.id === id);
      if (idx >= 0) this.$set(this.overviewItems, idx, { ...this.overviewItems[idx], ...changes });
      if (this.kpiView && this.kpiView.id === id) this.kpiView = { ...this.kpiView, ...changes };
      if (this.overviewExpandedId === id) this.overviewExpandMode = 'view';
    },
    onKpiDraft({ id, items }) {
      if (items) this.$set(this.kpiDrafts, id, items);
      else this.$delete(this.kpiDrafts, id);
    },
    mealItemNames(v) {
      return this.mealItems(v).map(x => x.name).filter(Boolean).join(', ');
    },
    mealTotalCalories(v) {
      const total = this.mealItems(v).reduce((sum, x) => sum + (Number(x.calorie) || 0), 0);
      return total ? Math.round(total) : '';
    },
    // Bấm lại đúng nút đang mở thì đóng; bấm nút khác trên cùng dòng thì chuyển chế độ
    toggleOverviewRow(v, mode) {
      if (this.overviewExpandedId === v.id && this.overviewExpandMode === mode) {
        this.overviewExpandedId = null;
        return;
      }
      this.overviewExpandedId = v.id;
      this.overviewExpandMode = mode;
      this.loadSessionThread(v.sessionId);
    },
    // Session có thể lẫn tin sau / chủ đề khác. Giữ chuỗi sinh ra chỉ số này: tới tin chỉ số, cùng chủ đề hoặc normal_chat.
    // Không cắt ở lần chốt trước: khách hỏi thêm làm chỉ số đổi thì record sau vẫn thấy ảnh + lần phân tích đầu
    threadOf(v) {
      const thread = this.sessionThreads[v.sessionId];
      if (!thread || thread.loading) return thread || { loading: true, items: [] };

      const at = new Date(v.createDatetime).getTime();
      const sameTopic = t => t.kpiType === v.kpiType || t.kpiType === 'normal_chat' || t.kpiType === 'non_target_chat';
      let items = thread.items.filter(t => t.id === v.id || (sameTopic(t) && new Date(t.createDatetime).getTime() <= at));

      // Chuỗi có ảnh thì bắt đầu từ ảnh gần nhất trước chỉ số (lần gửi ảnh mở đầu lần phân tích)
      // Ảnh của khách không chỉ định không phải ảnh của chỉ số này
      const imageIdx = items.map(t => !!t.imageUrl && t.kpiType !== 'non_target_chat').lastIndexOf(true);
      if (imageIdx > 0) items = items.slice(imageIdx);

      return { loading: false, items };
    },
    async loadSessionThread(sessionId) {
      if (!sessionId || (this.sessionThreads[sessionId] && !this.sessionThreads[sessionId].loading)) return;
      this.$set(this.sessionThreads, sessionId, { loading: true, items: [] });
      try {
        const body = (await decisionAiCoachService.getSessionMessages(sessionId)) || {};
        const items = Array.isArray(body) ? body : body.data || [];
        this.$set(this.sessionThreads, sessionId, { loading: false, items: items.map((x) => this.normalizeItem(x)) });
      } catch (error) {
        console.error('Failed to load session thread:', error);
        this.$set(this.sessionThreads, sessionId, { loading: false, items: [] });
      }
    },
    // Lưu nguyên như AI nhận diện. Muốn bỏ bớt dòng / sửa thì dùng nút Sửa
    async quickSaveKpi(v) {
      if (this.overviewBusyId) return;
      const payload = v.nutrition
        ? { nutrition: { ...v.nutrition, timeFrameId: v.nutrition.timeFrameId || DEFAULT_MEAL_TIME_FRAME } }
        : { glucoseRows: v.glucoseRows || [] };

      this.overviewBusyId = v.id;
      try {
        const body = (await decisionAiCoachService.saveKpi(v.id, payload)) || {};
        if (body.success) {
          this.onKpiHandled({ id: v.id, status: 1 });
          this.$toastr.s({ title: 'Thành công', msg: body.message || 'Đã lưu chỉ số' });
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: body.message || body.error || 'Lưu chỉ số thất bại' });
        }
      } catch (e) {
        console.error('[quickSaveKpi]', e);
        this.$toastr.e({ title: 'Lỗi', msg: 'Lưu chỉ số thất bại, vui lòng thử lại' });
      } finally {
        this.overviewBusyId = null;
      }
    },
    async skipFromOverview(v) {
      if (this.overviewBusyId) return;
      const ok = await this.$bvModal.msgBoxConfirm('Bỏ qua chỉ số AI nhận diện của tin này? Chỉ số vẫn hiển thị nhưng không lưu sang app.', {
        title: 'Bỏ qua chỉ số',
        okTitle: 'Bỏ qua',
        okVariant: 'danger',
        cancelTitle: 'Huỷ',
        centered: true,
      });
      if (!ok) return;

      this.overviewBusyId = v.id;
      try {
        const body = (await decisionAiCoachService.skipKpi(v.id)) || {};
        if (body.success) {
          this.onKpiHandled({ id: v.id, status: 2 });
          this.$toastr.s({ title: 'Thành công', msg: body.message || 'Đã bỏ qua chỉ số' });
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: body.message || body.error || 'Bỏ qua chỉ số thất bại' });
        }
      } catch (e) {
        console.error('[skipFromOverview]', e);
        this.$toastr.e({ title: 'Lỗi', msg: 'Bỏ qua chỉ số thất bại, vui lòng thử lại' });
      } finally {
        this.overviewBusyId = null;
      }
    },
    async openFromOverview(v) {
      this.showKpiOverview = false;
      if (this.selectedItem && this.selectedItem.id === v.id) return;
      const item = await this.fetchItem(v.id);
      if (item) await this.selectItem(item);
    },
    async fetchItem(id) {
      try {
        const data = await decisionAiCoachService.getById(id);
        return data;
      } catch (error) {
        console.error('Failed to fetch item:', error);
        return null;
      }
    },
    async markAsViewed(id) {
      try {
        await decisionAiCoachService.markAsViewed(id);
      } catch (error) {
        console.error('Failed to mark as viewed:', error);
      }
    },
    async loadApprovalStats(kpiType) {
      try {
        const params = {
          ...(kpiType ? { kpiType } : {}),
          ...(this.selectedCoachId ? { coachId: this.selectedCoachId } : {})
        };
        const result = await decisionAiCoachService.getApprovalStats(params);
        // Handle both camelCase and PascalCase
        this.approvalStats = {
          kpiType: result.kpiType || result.KpiType,
          total: result.total || result.Total || 0,
          approvedDirect: result.approvedDirect || result.ApprovedDirect || 0,
          approvedEdited: result.approvedEdited || result.ApprovedEdited || 0,
          rejected: result.rejected || result.Rejected || 0,
          approvedDirectPercent: result.approvedDirectPercent || result.ApprovedDirectPercent || 0,
          approvedEditedPercent: result.approvedEditedPercent || result.ApprovedEditedPercent || 0,
          rejectedPercent: result.rejectedPercent || result.RejectedPercent || 0,
          avgEditPercent: result.avgEditPercent || result.AvgEditPercent || 0,
          avgResponseTime: result.avgResponseTime || result.AvgResponseTime || '—',
        };
      } catch (error) {
        console.error('Failed to load approval stats:', error);
        this.approvalStats = null;
      }
    },
    // Lỗi lưu lý do không được chặn việc gửi hoặc bỏ qua tin
    async saveReviewFeedback(action) {
      if (!this.reviewReason && !this.reviewNote.trim()) return;
      try {
        await decisionAiCoachService.saveFeedback({
          decisionId: this.selectedItem.id,
          action,
          reason: this.reviewReason,
          note: this.reviewNote,
        });
      } catch (error) {
        console.error('[saveReviewFeedback]', error);
        this.$toastr.w({ title: 'Chưa lưu được lý do', msg: 'Tin vẫn được xử lý, nhưng lý do chưa lưu' });
      }
    },
    async approveAndSend() {
      if (!this.editMessage.trim()) return;
      this.sending = true;
      try {
        await this.saveReviewFeedback('send');
        const result = await decisionAiCoachService.send({
          id: this.selectedItem.id,
          message: this.editMessage,
          coachNote: this.coachNote,
          // API là AllowAnonymous nên server không đọc được coach từ token -> phải gửi kèm,
          // thiếu thì reviewed_by trống và bản ghi không vào được store văn phong
          reviewedBy: this.currentUser?.id || this.currentUser?.Id || null
        });
        if (result.success) {
          this.$toastr.s({ title: 'Thành công', msg: 'Đã gửi tin nhắn!' });
          this.selectedItem = null;
          this.loadData();
          this.loadStats();
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: result.error || 'Gửi thất bại' });
        }
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể gửi' });
      } finally {
        this.sending = false;
      }
    },
    async rejectMessage() {
      this.sending = true;
      try {
        const result = await decisionAiCoachService.reject({
          id: this.selectedItem.id,
          coachNote: this.coachNote,
          reviewedBy: this.currentUser?.id || this.currentUser?.Id || null
        });
        if (result.success) {
          this.$toastr.s({ title: 'Thành công', msg: 'Đã từ chối tin nhắn' });
          this.selectedItem = null;
          this.loadData();
          this.loadStats();
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: result.error || 'Thao tác thất bại' });
        }
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể từ chối' });
      } finally {
        this.sending = false;
      }
    },
    async handleSkip() {
      if (!this.selectedItem) return;
      if (!this.reviewReason) {
        this.$toastr.w({ title: 'Cần lý do', msg: 'Chọn lý do bỏ qua trước nhé' });
        return;
      }
      this.skipping = true;
      try {
        await this.saveReviewFeedback('skip');
        const result = await decisionAiCoachService.skip(this.selectedItem.id);
        if (result.success) {
          this.$toastr.s({ title: 'Thành công', msg: 'Đã bỏ qua tin nhắn' });
          this.selectedItem = null;
          this.loadData();
          this.loadStats();
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: result.error || 'Thao tác thất bại' });
        }
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể bỏ qua' });
      } finally {
        this.skipping = false;
      }
    },
    handleSend() {
      if (this.selectedItem.status === 3 || this.selectedItem.status === 2) {
        this.resendMessage();
      } else {
        this.approveAndSend();
      }
    },
    async resendMessage() {
      if (!this.editMessage.trim()) return;
      this.sending = true;
      try {
        const result = await decisionAiCoachService.send({
          id: this.selectedItem.id,
          message: this.editMessage,
          reviewedBy: this.currentUser?.id || this.currentUser?.Id || null
        });
        if (result.success) {
          this.$toastr.s({ title: 'Thành công', msg: 'Đã gửi lại tin nhắn!' });
          this.loadData();
          this.loadStats();
          // Reload selected item
          this.selectedItem.editedMessage = this.editMessage;
          this.selectedItem.sentAt = new Date().toISOString();
        } else {
          this.$toastr.e({ title: 'Lỗi', msg: result.error || 'Gửi lại thất bại' });
        }
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể gửi lại' });
      } finally {
        this.sending = false;
      }
    },
    getStatusColor(status) {
      const colors = { 0: '#BA7517', 1: '#378ADD', 2: '#A32D2D', 3: '#0F6E56' };
      return colors[status] || '#888780';
    },
    getStatusLabel(status) {
      const labels = { 0: 'Chờ duyệt', 1: 'Đã chỉnh sửa', 2: 'Từ chối', 3: 'Đã gửi', 4: 'Bỏ qua' };
      return labels[status] || 'Unknown';
    },
    getSharedQuestion(sharedContentAsk) {
      return sharedContentAsk || '';
    },
    getItemResponseTime() {
      if (!this.selectedItem) return '—';
      const sentAt = this.selectedItem.sentAt || this.selectedItem.SentAt;
      const createAt = this.selectedItem.createDatetime || this.selectedItem.CreateDatetime;
      if (!sentAt || !createAt) return 'Chưa gửi';

      const sent = this.parseUtcDate(sentAt);
      const created = this.parseUtcDate(createAt);
      if (!sent || !created) return '—';

      const diffMs = sent - created;
      if (diffMs <= 0) return '< 1 phút';

      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) return '< 1 phút';
      if (minutes < 60) return `${minutes} phút`;

      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours} giờ`;

      const days = Math.floor(hours / 24);
      return `${days} ngày`;
    },
    getResponseTimePercent() {
      if (!this.selectedItem) return 0;
      const sentAt = this.selectedItem.sentAt || this.selectedItem.SentAt;
      if (!sentAt) return 0;
      return 100; // Full bar if sent
    },
    getEditPercentClass() {
      const percent = this.getItemEditPercent();
      if (percent === 0) return 'edit-none';
      if (percent <= 30) return 'edit-low';
      return 'edit-high';
    },
    getItemEditPercent() {
      if (!this.selectedItem) return 0;
      const original = this.selectedItem.aiResponse || '';
      const edited = this.selectedItem.editedMessage || '';
      if (!edited || original === edited) return 0;

      // Levenshtein distance calculation
      const levenshtein = (a, b) => {
        const matrix = [];
        for (let i = 0; i <= b.length; i++) {
          matrix[i] = [i];
        }
        for (let j = 0; j <= a.length; j++) {
          matrix[0][j] = j;
        }
        for (let i = 1; i <= b.length; i++) {
          for (let j = 1; j <= a.length; j++) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
              matrix[i][j] = matrix[i - 1][j - 1];
            } else {
              matrix[i][j] = Math.min(
                matrix[i - 1][j - 1] + 1,
                matrix[i][j - 1] + 1,
                matrix[i - 1][j] + 1
              );
            }
          }
        }
        return matrix[b.length][a.length];
      };

      const distance = levenshtein(original, edited);
      const maxLen = Math.max(original.length, edited.length);
      if (maxLen === 0) return 0;
      return Math.round((distance / maxLen) * 100);
    },
    getKpiLabel(kpiType) {
      return kpiMeta(kpiType).label;
    },
    getKpiColor(kpiType) {
      return kpiMeta(kpiType).color;
    },
    getKpiChipStyle(kpiType) {
      const m = kpiMeta(kpiType);
      return { background: m.bg, color: m.color };
    },
    getKpiTagStyle(kpiType) {
      const m = kpiMeta(kpiType);
      return { background: m.bg, color: m.color, border: `0.5px solid ${m.border}` };
    },
    getAvatarStyle(kpiType) {
      const m = kpiMeta(kpiType);
      return { background: m.avatarBg, color: m.avatarColor };
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },
    getGroupName(groupId) {
      const group = this.allGroups.find(g => g.group_id === groupId);
      return group ? group.name : groupId;
    },
    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    parseUtcDate(dateVal) {
      if (!dateVal) return null;
      if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;

      // Handle timestamp (number)
      if (typeof dateVal === 'number') {
        // If timestamp is in seconds (< year 3000 in ms), convert to ms
        const ts = dateVal < 10000000000 ? dateVal * 1000 : dateVal;
        const date = new Date(ts);
        return isNaN(date.getTime()) ? null : date;
      }

      // Handle string
      let str = String(dateVal);
      let date = new Date(str);
      if (isNaN(date.getTime()) && !str.endsWith('Z')) {
        date = new Date(str + 'Z');
      }
      return isNaN(date.getTime()) ? null : date;
    },
    formatDate(dateStr) {
      const date = this.parseUtcDate(dateStr);
      if (!date) return '-';
      return date.toLocaleDateString('vi-VN');
    },
    formatDateTime(dateStr) {
      const date = this.parseUtcDate(dateStr);
      if (!date) return '-';
      return date.toLocaleString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatTimeAgo(dateStr) {
      const date = this.parseUtcDate(dateStr);
      if (!date) return '-';
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Vừa xong';
      if (minutes < 60) return `${minutes} phút`;
      if (hours < 24) return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
      if (days === 1) return 'Hôm qua';
      return date.toLocaleDateString('vi-VN');
    },

    async loadChatHistory(accountId) {
      if (!accountId) return;
      this.chatHistoryLoading = true;
      this.chatHistoryMessages = [];
      this.chatHistoryGroupName = '';
      try {
        const res = await decisionAiCoachService.getZaloChatHistory(accountId);
        // webhook.service returns res.data directly, so res is the array
        const conversations = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        const allMessages = [];
        conversations.forEach(conv => {
          if (!this.chatHistoryGroupName && (conv.groupName || conv.recipient)) {
            this.chatHistoryGroupName = conv.groupName || conv.recipient;
          }
          (conv.messages || []).forEach(msg => {
            // Lưu cả 2 URL, ưu tiên Zalo trước, fallback VNG khi Zalo hết hạn (qua @error)
            // BE đánh dấu backup thất bại bằng 'expired_link'/'not_found' (TokenRefreshService.BackupMediaZalo),
            // không phải 'EXPIRED' - phải khớp đúng 2 giá trị này thì mới lọc được link rác ra khỏi fallback.
            const zaloUrl = msg.imageUrl || null;
            const vngUrl = msg.vngUrl && msg.vngUrl !== 'expired_link' && msg.vngUrl !== 'not_found' ? msg.vngUrl : null;
            allMessages.push({
              id: msg.id,
              message: msg.message || '',
              imageUrl: zaloUrl || vngUrl,
              fallbackUrl: zaloUrl ? vngUrl : null,
              timestamp: Number(msg.timestamp),
              senderType: (msg.event_name || '').includes('oa_') ? 'oa' : 'user',
              userName: msg.userName || '',
              eventName: msg.event_name,
            });
          });
        });
        allMessages.sort((a, b) => a.timestamp - b.timestamp);
        this.chatHistoryMessages = allMessages;
        this.$nextTick(() => {
          ['chatHistoryBody', 'mobileChatBody'].forEach(ref => {
            const el = this.$refs[ref];
            if (el) el.scrollTop = el.scrollHeight;
          });
        });
      } catch (e) {
        this.chatHistoryMessages = [];
      } finally {
        this.chatHistoryLoading = false;
      }
    },

    toggleDesktopChat() {
      this.mobileChatOpen = !this.mobileChatOpen;
      if (this.mobileChatOpen) {
        this.$nextTick(() => {
          const el = this.$refs.mobileChatBody;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    },

    openMobileChat() {
      this.mobileChatOpen = true;
      this.$nextTick(() => {
        const el = this.$refs.mobileChatBody;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    formatChatTime(timestamp) {
      if (!timestamp) return '';
      const d = new Date(timestamp);
      return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style lang="scss" scoped>
.coach-cms {
  // Khung cha là flex: không đặt width thì trang co theo nội dung tin, tin ngắn bị hở bên phải
  width: 100%;
  min-width: 0;
  height: calc(100vh - 65px);
  background: #f5f5f5;
}

.shell {
  display: grid;
  grid-template-columns: 200px 320px minmax(0, 1fr);
  grid-template-rows: 46px 1fr;
  width: 100%;
  height: 100%;
  border: 0.5px solid #e5e5e5;
  border-radius: 12px;
  overflow: hidden;
  margin: 0;
}

// Topbar
.topbar {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-bottom: 0.5px solid #e5e5e5;
  background: #fff;
}

.logo {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  letter-spacing: -0.01em;

  span {
    color: #1D9E75;
  }
}

.tbadge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 99px;
}

.tb-warn {
  background: #FAEEDA;
  color: #854F0B;
  border: 0.5px solid #FAC775;
}

.tb-ok {
  background: #EAF3DE;
  color: #3B6D11;
  border: 0.5px solid #C0DD97;
}

.tbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.av {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
}

.av-coach {
  background: #E1F5EE;
  color: #0F6E56;
}

// Sidebar
.sidebar {
  border-right: 0.5px solid #e5e5e5;
  background: #fafafa;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
}

.coach-filter-wrapper {
  padding: 0 8px;
  margin-bottom: 16px;
}

.sec-lbl {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #999;
  padding: 8px 8px 4px;
}

.nav {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: #fff;
    color: #333;
  }

  &.on {
    background: #fff;
    color: #0F6E56;
    font-weight: 500;
    border: 0.5px solid #e5e5e5;
  }
}

.ndot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.nc {
  margin-left: auto;
  font-size: 10px;
  font-weight: 600;
  background: #E1F5EE;
  color: #0F6E56;
  border-radius: 99px;
  padding: 1px 6px;
}

.nc-gray {
  background: #F1EFE8;
  color: #5F5E5A;
}

.nc-purple {
  background: #F5EEF8;
  color: #6C3483;
}

.nc-red {
  background: #FCEBEB;
  color: #A32D2D;
}

// Patient List
.kh-list {
  border-right: 0.5px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.kh-header {
  padding: 10px 12px;
  border-bottom: 0.5px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #fafafa;
}

.kh-title {
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

.search-mini {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 0.5px solid #e5e5e5;
  border-radius: 6px;
  padding: 5px 9px;
  font-size: 11px;
  color: #999;

  input {
    border: none;
    outline: none;
    font-size: 11px;
    width: 100%;
    background: transparent;

    &::placeholder {
      color: #bbb;
    }
  }
}

.kh-items {
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 99px;
  }
}

.kh-row {
  padding: 10px 12px;
  border-bottom: 0.5px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: #fafafa;
  }

  &.sel {
    background: #E1F5EE;
    border-left: 2.5px solid #1D9E75;
  }
}

.kh-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.kh-av {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kh-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  flex: 1;
}

.kh-time {
  font-size: 10px;
  color: #999;
}

.kh-last {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 5px;
}

.topic-chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.topic {
  font-size: 9px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 99px;
}

.unread-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1D9E75;
  flex-shrink: 0;
}

.acc-group-shared-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  color: #4338CA;
  flex-shrink: 0;
}

.kh-more {
  padding: 8px 10px 12px;
}

.empty-list {
  padding: 40px 20px;
  text-align: center;
  color: #999;
}

// Accordion Styles
.acc-group {
  border-bottom: 0.5px solid #f0f0f0;
}

.acc-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: #fafafa;
  }

  &.expanded {
    background: #f8f8f8;
  }
}

.acc-arrow {
  flex-shrink: 0;
  transition: transform 0.2s;
  color: #999;

  &.rotated {
    transform: rotate(90deg);
  }
}

.acc-group-info {
  flex: 1;
  min-width: 0;
}

.acc-group-meta {
  font-size: 10px;
  color: #999;
}

.acc-kpis {
  background: #fafafa;
  border-top: 0.5px solid #f0f0f0;
}

.acc-kpi-groups {
  background: #fafafa;
}

.acc-kpi-group {
  border-bottom: 0.5px solid #e8e8e8;

  &:last-child {
    border-bottom: none;
  }
}

.acc-kpi-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 10px 20px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.1s;
  background: #f8f8f8;

  &:hover {
    background: #f0f0f0;
  }

  &.expanded {
    background: #eee;
  }
}

.kpi-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.acc-kpi-label {
  font-weight: 500;
  color: #333;
}

.acc-kpi-meta {
  color: #888;
  font-size: 11px;
  margin-left: auto;
}

.kpi-pending-badge {
  background: #BA7517;
  color: white;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 8px;
  font-weight: 500;
  margin-left: 4px;
}

.acc-kpi {
  border-bottom: 0.5px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.acc-kpi-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 8px 28px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.1s;

  &:hover {
    background: #f5f5f5;
  }

  &.expanded {
    background: #f0f0f0;
  }
}

.acc-kpi-name {
  color: #444;
  font-weight: 500;
}

.acc-kpi-count {
  color: #999;
  font-size: 10px;
}

.acc-dates {
  background: #f8f8f8;
}

.acc-date {
  border-bottom: 0.5px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.acc-date-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 6px 36px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  transition: background 0.1s;

  &:hover {
    background: #f0f0f0;
  }

  &.expanded {
    background: #eee;
  }
}

.acc-date-text {
  font-weight: 500;
  color: #555;
}

.acc-date-count {
  color: #999;
  font-size: 10px;
}

.acc-messages {
  background: #fff;
}

.acc-messages-flat {
  padding-left: 0;

  .acc-msg {
    padding-left: 20px;
  }
}

.acc-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 52px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.1s;
  border-bottom: 0.5px solid #f8f8f8;

  &:hover {
    background: #f8f8f8;
  }

  &.sel {
    background: #E1F5EE;
    border-left: 2px solid #1D9E75;
  }

  &:last-child {
    border-bottom: none;
  }
}

.acc-msg-status {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.acc-msg-text {
  flex: 1;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.acc-msg-time {
  font-size: 10px;
  color: #999;
  flex-shrink: 0;
}

.acc-msg-shared-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  color: #4338CA;
  border: 1px solid #A5B4FC;
  flex-shrink: 0;
}

.acc-msg-shared-tag svg {
  flex-shrink: 0;
}

// Detail Panel
.detail {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  height: 100%;
  background: #fff;
}

.detail-empty {
  align-items: center;
  justify-content: center;
}

.detail-dashboard {
  padding: 24px;
  overflow-y: auto;
}

.dashboard-header {
  margin-bottom: 24px;
}

.dashboard-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.dashboard-subtitle {
  font-size: 13px;
  color: #888;
  margin: 0;
}

.dashboard-stats-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.dash-stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  background: #fff;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }

  &.pending {
    border-left: 3px solid #BA7517;
    .dash-stat-icon { background: #FAEEDA; color: #854F0B; }
  }

  &.edited {
    border-left: 3px solid #378ADD;
    .dash-stat-icon { background: #E6F1FB; color: #185FA5; }
  }

  &.sent {
    border-left: 3px solid #1D9E75;
    .dash-stat-icon { background: #E1F5EE; color: #0F6E56; }
  }

  &.today {
    border-left: 3px solid #7F77DD;
    .dash-stat-icon { background: #EEEDFE; color: #3C3489; }
  }

  &.shared {
    border-left: 3px solid #9B59B6;
    .dash-stat-icon { background: #F5EEF8; color: #6C3483; }
  }
}

.dash-stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.dash-stat-info {
  flex: 1;
}

.dash-stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.dash-stat-label {
  font-size: 12px;
  color: #888;
}

.dashboard-section {
  background: #fafafa;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h5 {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.section-badge {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 99px;
  background: #E1F5EE;
  color: #0F6E56;
}

.enabled-groups-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.enabled-group-chip {
  font-size: 11px;
  padding: 5px 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e5e5e5;
  color: #555;

  &.more {
    background: #f0f0f0;
    color: #888;
  }
}

.no-groups-msg {
  p {
    font-size: 13px;
    color: #888;
    margin: 0;
  }
}

.dashboard-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #f8f8f8;
  border-radius: 8px;
  border: 1px dashed #ddd;

  svg {
    flex-shrink: 0;
    color: #bbb;
  }

  span {
    font-size: 13px;
    color: #888;
  }
}

.empty-content {
  text-align: center;
  color: #999;

  svg {
    margin-bottom: 16px;
    stroke: #ccc;
  }

  h5 {
    color: #666;
    margin-bottom: 8px;
    font-size: 14px;
  }

  p {
    font-size: 12px;
  }
}

.d-header {
  padding: 10px 14px;
  border-bottom: 0.5px solid #e5e5e5;
  display: flex;
  align-items: center;
  gap: 10px;
}

.shared-content-banner {
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  border-bottom: 1px solid #A5B4FC;
  padding: 10px 14px;
  flex-shrink: 0;
}

.shared-content-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #4338CA;
  margin-bottom: 6px;

  svg {
    flex-shrink: 0;
  }

  strong {
    font-weight: 600;
  }
}

.shared-content-body {
  font-size: 13px;
  color: #1e1b4b;
  line-height: 1.5;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
  padding: 8px 10px;
  border: 1px solid rgba(165, 180, 252, 0.5);
}

.detail-tabs {
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  background: #fafafa;
  flex-shrink: 0;
}

.detail-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  font-size: 12px;
  font-weight: 500;
  color: #888;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: #333;
  }

  &.active {
    color: #1D9E75;
    border-bottom-color: #1D9E75;
  }
}

.msg-tab-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 99px;
  }
}

.msg-tab-input {
  border-top: 1px solid #e5e5e5;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.msg-tab-textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: #333;
  resize: none;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: #bbb; }
  &:focus { border-color: #5DCAA5; }
}

.msg-tab-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.msg-tab-hint {
  font-size: 11px;
  color: #bbb;
}

.d-av {
  width: 34px;
  height: 34px;
  font-size: 11px;
}

.d-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.d-shared-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 99px;
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  color: #4338CA;
  border: 1px solid #A5B4FC;
  white-space: nowrap;
}

.d-shared-tag svg {
  flex-shrink: 0;
}

.d-meta {
  font-size: 11px;
  color: #999;
}

.d-actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
}

.btn {
  font-size: 13px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: transparent;
  color: #666;
  transition: all 0.12s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #fafafa;
    color: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-approve {
  background: #E1F5EE;
  color: #0F6E56;
  border-color: #5DCAA5;

  &:hover {
    background: #9FE1CB;
    color: #085041;
  }
}

.btn-edit-send {
  background: #E6F1FB;
  color: #185FA5;
  border-color: #85B7EB;
}

.btn-reject {
  background: #FCEBEB;
  color: #A32D2D;
  border-color: #F09595;
}

.review-feedback {
  display: flex;
  flex: 1;
  gap: 8px;
  margin: 0 8px;
  min-width: 0;
  &__reason { max-width: 230px; flex-shrink: 0; }
  &--mobile { flex-direction: column; margin: 8px 0; }
  &--mobile &__reason { max-width: none; }
}
.btn-skip {
  background: #fff;
  color: #A32D2D;
  border-color: #F09595;
}

.btn-share {
  background: #EEF2FF;
  color: #4338CA;
  border-color: #A5B4FC;

  &:hover {
    background: #C7D2FE;
    color: #312E81;
  }
}

.compare-collapse-toggle {
  display: none;
}

.stats-collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  user-select: none;
  border-bottom: 0.5px solid #f0f0f0;

  &:hover { color: #555; }
}

// display: contents giữ nguyên bố cục cũ của stats-row / approval-stats-row khi mở
.stats-collapsible {
  display: none;

  &.stats-collapsible--open {
    display: contents;
  }
}

@media (max-width: 767px) {
  .stats-collapse-toggle,
  .compare-collapse-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    user-select: none;
    border-bottom: 0.5px solid #f0f0f0;
    -webkit-tap-highlight-color: transparent;
  }

  .compare-section--hidden,
  .original-message-box--hidden {
    display: none !important;
  }

  .stats-collapsible {
    display: none;
    overflow: hidden;
    &.stats-collapsible--open {
      display: block;
      max-height: 35vh;
      overflow-y: auto;
    }
  }

  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

// Stats Row
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: #e5e5e5;
  border-bottom: 0.5px solid #e5e5e5;
}

.stat {
  background: #fff;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.stat__v {
  font-size: 16px;
  font-weight: 500;
}

.stat__l {
  font-size: 10px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.bar-bg {
  height: 3px;
  background: #f5f5f5;
  border-radius: 99px;
  margin-top: 4px;
}

.bar-fill {
  height: 3px;
  border-radius: 99px;
}

// Approval Stats Row
.approval-stats-row {
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 0.5px solid #e5e5e5;
}

.approval-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  margin-bottom: 8px;
}

.approval-bars {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.approval-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  min-width: 180px;
}

.approval-label {
  color: #666;
  width: 85px;
  flex-shrink: 0;
}

.approval-track {
  flex: 1;
  height: 6px;
  background: #e5e5e5;
  border-radius: 99px;
  min-width: 60px;
}

.approval-fill {
  height: 6px;
  border-radius: 99px;
  transition: width 0.3s ease;
}

.approval-value {
  font-weight: 600;
  min-width: 50px;
  text-align: right;
  white-space: nowrap;
}


// Detail Body
.d-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;

  // Không cho con bị bóp (tin có ảnh + chỉ số thì panel chỉ số dẹp lại) - dài quá thì d-body cuộn
  > * { flex-shrink: 0; }
}

// Compare Section (AI Response vs Edited side-by-side)
.compare-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: #e5e5e5;
  border-bottom: 0.5px solid #e5e5e5;
  height: 220px;
  flex-shrink: 0;
}

.compare-col {
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.compare-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  padding: 8px 12px;
  border-bottom: 0.5px solid #e5e5e5;
  background: #fafafa;
  display: flex;
  align-items: center;
  gap: 6px;
}

.compare-icon {
  font-size: 9px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;

  &.ai {
    background: #EEEDFE;
    color: #3C3489;
  }

  &.edited {
    background: #E6F1FB;
    color: #185FA5;
  }
}

.edit-badge {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 99px;
  background: #FAEEDA;
  color: #854F0B;
  margin-left: auto;
}

.compare-content {
  flex: 1;
  padding: 10px 12px;
  font-size: 12px;
  color: #333;
  line-height: 1.65;
  overflow-y: auto;
  white-space: pre-wrap;

  &.no-edit {
    color: #999;
    font-style: italic;
  }

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 99px;
  }
}

// Edit & Send Section
.edit-send-section {
  background: #fff;
  border-top: 0.5px solid #e5e5e5;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.send-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #666;
  padding: 6px 10px;
  background: #fafafa;
  border-radius: 6px;
}

.status-icon {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;

  &.success {
    background: #E1F5EE;
    color: #0F6E56;
  }

  &.danger {
    background: #FCEBEB;
    color: #A32D2D;
  }
}

.status-time {
  color: #999;
}

// Modal tổng quan chỉ số: chủ đề dọc bên trái, bảng bên phải
.kov {
  display: flex;
  gap: 20px;
  min-height: 60vh;
}

.kov-nav {
  flex: 0 0 250px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding-right: 16px;
  border-right: 1px solid #EEF2F0;

  &__head {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__search {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border-radius: 8px;
    font-size: 13px;
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 12px;
    color: #555;
    cursor: pointer;

    input { accent-color: #1D9E75; }
  }

  &__empty {
    padding: 24px 0;
    text-align: center;
    font-size: 12px;
    color: #999;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: 62vh;
    overflow-y: auto;
  }

  &__avatar {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #E1F5EE;
    color: #0F6E56;
    font-size: 11px;
    font-weight: 600;
    line-height: 30px;
    text-align: center;
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__pending {
    flex-shrink: 0;
    min-width: 22px;
    padding: 0 7px;
    border-radius: 99px;
    background: #FAEEDA;
    color: #854F0B;
    font-size: 11px;
    font-weight: 600;
    line-height: 20px;
    text-align: center;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: #333;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: #F4FBF8; }

    &.active {
      background: #E1F5EE;
      border-color: #BFE6D6;
      color: #0F6E56;
      font-weight: 600;

      .kov-nav__avatar { background: #1D9E75; color: #fff; }
    }
  }

  &__count {
    font-size: 11px;
    color: #999;
    font-weight: 400;
  }
}

.kov-main {
  flex: 1;
  min-width: 0;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__name {
    font-size: 17px;
    font-weight: 600;
    color: #1a1a1a;
  }
}

// Chủ đề dạng segmented
.kov-topics {
  display: flex;
  gap: 2px;
  padding: 3px;
  border-radius: 10px;
  background: #F1F5F3;
}

.kov-topic {
  padding: 5px 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #555;
  font-size: 13px;
  cursor: pointer;

  &.active {
    background: #fff;
    color: #0F6E56;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  &__count {
    display: inline-block;
    min-width: 18px;
    margin-left: 4px;
    padding: 0 5px;
    border-radius: 9px;
    color: #999;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;

    // Có record thì badge đỏ cho coach chú ý
    &.has-items {
      background: #E24B4A;
      color: #fff;
    }
  }
}

.kov-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.kov-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.kov-chip {
  padding: 5px 12px;
  border: 1px solid #E3EAE6;
  border-radius: 99px;
  background: #fff;
  color: #555;
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  b { margin-left: 3px; font-weight: 600; }

  &:hover { border-color: #1D9E75; }

  &.active { border-color: #1D9E75; background: #E1F5EE; color: #0F6E56; }
  &--pending b { color: #854F0B; }
  &--saved b { color: #0F6E56; }
  &--skipped b { color: #888780; }
}

.kov-filters {
  display: flex;
  align-items: center;
  gap: 12px;

  &__toggle {
    font-size: 12px;
    white-space: nowrap;
  }
}

.kov-dropdown ::v-deep .btn {
  height: 30px;
  padding: 0 12px;
  border-color: #E3EAE6;
  border-radius: 8px;
  background: #fff;
  color: #444;
  font-size: 12px;
}

.kov-dropdown ::v-deep .dropdown-item.active,
.kov-dropdown ::v-deep .dropdown-item:active {
  background: #E1F5EE;
  color: #0F6E56;
}

.kov-select {
  height: 30px;
  font-size: 12px;
  padding: 0 8px;
  border: 1px solid #E3EAE6;
  border-radius: 8px;
  background: #fff;
}

.kov-empty {
  padding: 56px 0;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.kov-table {
  width: 100%;
  font-size: 13px;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #EEF2F0;
  border-radius: 10px;
  overflow: hidden;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #F7FAF8;
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 10px 12px;
    border-bottom: 1px solid #EEF2F0;
    white-space: nowrap;
  }

  td {
    padding: 12px;
    border-bottom: 1px solid #F1F4F2;
    vertical-align: middle;
    color: #333;
  }

  tbody tr:last-child td { border-bottom: none; }

  &__row {
    transition: background 0.15s;

    &:hover td { background: #FAFCFB; }
    &.current td { background: #F4FBF8; }
    &.expanded td { border-bottom-color: transparent; background: #F4FBF8; }
  }

  &__time { white-space: nowrap; }

  &__msg {
    max-width: 300px;
    color: #555;
    font-size: 12px;
    line-height: 1.5;
    word-break: break-word;
  }

  &__actions {
    width: 1%; // co cột vừa đủ hàng nút
    white-space: nowrap;
  }

  &__detail td {
    background: #F4FBF8;
    padding: 4px 12px 16px;
  }
}

.kov-time {
  &__hm { font-weight: 600; color: #333; }
  &__date { font-size: 11px; color: #999; }
}

.kov-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
}

.kov-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #E3EAE6;
  border-radius: 8px;
  background: #fff;
  color: #666;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover:not(:disabled),
  &.active {
    border-color: #1D9E75;
    background: #E1F5EE;
    color: #0F6E56;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &__text { font-size: 12px; font-weight: 600; }

  &--save {
    padding: 0 12px;
    border-color: #1D9E75;
    background: #1D9E75;
    color: #fff;

    &:hover:not(:disabled) {
      border-color: #0F6E56;
      background: #0F6E56;
      color: #fff;
    }
  }

  &--danger:hover:not(:disabled) {
    border-color: #E8B4B4;
    background: #FCEBEB;
    color: #A32D2D;
  }

  .btn-spinner {
    width: 12px;
    height: 12px;
  }
}

.kov-measure {
  white-space: nowrap;

  & + & { margin-top: 4px; }

  &__tf {
    display: inline-block;
    min-width: 66px;
    color: #777;
    font-size: 12px;
  }

  &__at {
    margin-left: 6px;
    color: #aaa;
    font-size: 11px;
  }
}

.kov-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &--pending { background: #FAEEDA; color: #854F0B; }
  &--saved { background: #E1F5EE; color: #0F6E56; }
  &--skipped { background: #F1F1EF; color: #888780; }
}

.kov-detail__msg {
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #F3F6FB;
  border-left: 3px solid #378ADD;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  white-space: pre-wrap;
}

.kov-detail__label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #185FA5;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
}

.kov-thread {
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #F3F6FB;
  border-left: 3px solid #378ADD;
  border-radius: 4px;
  font-size: 12px;
  color: #333;

  &__empty { color: #999; font-style: italic; }

  &__node {
    position: relative;
    margin-left: 6px;
    padding: 6px 0 6px 12px;
    border-left: 2px solid #D6E2F0;

    &.current {
      border-left-color: #1D9E75;
      background: #EAF6F1;
      border-radius: 0 4px 4px 0;
    }
  }

  &__meta { font-size: 11px; color: #888; margin-bottom: 2px; }
  &__status { font-weight: 500; }
  &__tag {
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 99px;
    background: #1D9E75;
    color: #fff;
    font-size: 10px;
  }
  &__patient, &__reply { white-space: pre-wrap; word-break: break-word; }
  &__reply { margin-top: 3px; color: #555; }
}

.kov-detail__open {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  border: 1px solid #1D9E75;
  background: #fff;
  color: #0F6E56;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    background: #E1F5EE;
    color: #0F6E56;
  }
}

@media (max-width: 767px) {
  .kov {
    flex-direction: column;
    gap: 10px;
    min-height: 0;
  }

  // Khách hàng: ô tìm + hàng chip cuộn ngang, dính trên cùng khi cuộn danh sách chỉ số
  .kov-nav {
    position: sticky;
    top: -16px; // bù padding của modal-body
    z-index: 2;
    flex: none;
    margin: -16px -16px 0;
    padding: 10px 16px 8px;
    background: #fff;
    border-right: none;
    border-bottom: 1px solid #EEF2F0;

    &__head {
      flex-direction: row;
      align-items: center;
      margin-bottom: 4px;
    }

    &__search {
      flex: 1;
      min-height: 36px;
      font-size: 14px;
    }

    &__toggle {
      flex-shrink: 0;
    }

    &__list {
      flex-direction: row;
      gap: 6px;
      max-height: none;
      overflow-x: auto;
      overflow-y: hidden;
    }

    &__item {
      width: auto;
      max-width: 70vw;
      flex-shrink: 0;
      gap: 8px;
      min-height: 36px;
      border: 1px solid #E3EFE9;
    }
  }

  .kov-main__head {
    flex-direction: column;
    align-items: flex-start;
  }

  .kov-topic {
    min-height: 34px;
  }

  .kov-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .kov-filters {
    .kov-select {
      flex: 1;
      min-height: 36px;
      font-size: 14px;
    }
  }

  // Bảng -> mỗi tin là 1 thẻ "nhãn: giá trị", nút thao tác dàn đều hàng dưới
  .kov-table {
    thead { display: none; }

    tr.kov-table__row {
      display: block;
      margin-bottom: 8px;
      padding: 8px 10px;
      border: 1px solid #E3EFE9;
      border-radius: 8px;

      &.expanded {
        margin-bottom: 0;
        border-bottom: none;
        border-radius: 8px 8px 0 0;
      }
    }

    tr.kov-table__row td {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 3px 0;
      border: none;
      background: transparent !important;
    }

    tr.kov-table__row td[data-label]::before {
      content: attr(data-label);
      flex-shrink: 0;
      font-size: 11px;
      color: #777;
    }

    .kov-table__msg {
      max-width: none;
      text-align: right;
    }

    .kov-table__actions {
      width: auto;
      justify-content: flex-end;
      padding-top: 8px !important;
      border-top: 1px solid #EEF2F0 !important;
      margin-top: 4px;
    }

    .kov-actions { gap: 6px; }

    .kov-icon-btn {
      width: 36px;
      height: 36px;
    }

    tr.kov-table__detail {
      display: block;
      margin-bottom: 8px;
      border: 1px solid #E3EFE9;
      border-top: none;
      border-radius: 0 0 8px 8px;

      td {
        display: block;
        padding: 4px 10px 12px;
      }
    }
  }

  .kov-measure {
    white-space: normal;
    text-align: right;

    &__at { display: block; margin-left: 0; }
  }
}

// Nhãn trên danh sách: tin có chỉ số AI nhận diện mà coach chưa lưu / bỏ qua
.acc-msg-kpi-tag {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 99px;
  color: #0F6E56;
  background: #E1F5EE;
}

.d-kpi-editor {
  margin-top: 10px;
}

// Ảnh khách hàng gửi: thumbnail, bấm để xem to
.patient-images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.patient-image {
  display: block;
  max-width: 220px;
  max-height: 180px;
  margin: 4px 0 6px;
  border-radius: 6px;
  border: 1px solid #E3E8EF;
  object-fit: cover;
  cursor: zoom-in;
}

.chp-msg__img-expired {
  padding: 6px 10px;
  border-radius: 6px;
  background: #F3F3F1;
  color: #888780;
  font-size: 12px;
  font-style: italic;
}

.original-message-box {
  padding: 8px 10px;
  background: #F3F6FB;
  border-left: 3px solid #378ADD;
  border-radius: 4px;
}

.original-message-label {
  font-size: 10px;
  color: #185FA5;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 3px;
}

.original-message-text {
  font-size: 13px;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
}

.edit-percent-badge {
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 99px;

  &.edit-none {
    background: #E1F5EE;
    color: #0F6E56;
  }

  &.edit-low {
    background: #FAEEDA;
    color: #854F0B;
  }

  &.edit-high {
    background: #FCEBEB;
    color: #A32D2D;
  }
}

.edit-textarea-wrap {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: #1D9E75;
    box-shadow: 0 0 0 2px rgba(29, 158, 117, 0.1);
  }
}

.edit-textarea {
  width: 100%;
  border: none;
  padding: 14px 16px;
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  font-family: inherit;
  min-height: 150px;

  &::placeholder {
    color: #bbb;
  }
}

.edit-charcount {
  padding: 0 16px 8px;
  text-align: right;
  font-size: 11px;
  color: #bbb;

  &.near-limit {
    color: #e67e22;
    font-weight: 600;
  }

  &.over-limit {
    color: #A32D2D;
  }
}

.edit-send-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spacer {
  flex: 1;
}

.hint-text {
  font-size: 10px;
  color: #bbb;
}

.btn-reset {
  background: #f5f5f5;
  color: #666;
  border-color: #e5e5e5;

  &:hover {
    background: #e5e5e5;
    color: #333;
  }
}

.btn-reject {
  background: #FCEBEB;
  color: #A32D2D;
  border-color: #F09595;

  &:hover {
    background: #f5d5d5;
  }
}

.btn-skip {
  background: #fff;
  color: #A32D2D;
  border-color: #F09595;

  &:hover {
    background: #FCEBEB;
  }
}

.btn-send {
  background: #1D9E75;
  color: #fff;
  border-color: #1D9E75;
  padding: 6px 16px;

  &:hover:not(:disabled) {
    background: #168a63;
  }

  &:disabled {
    background: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
}

.btn-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 4px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.mini-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 2px;
}

.mini-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #666;
}

.mini-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.conv-col {
  display: flex;
  flex-direction: column;
  border-right: 0.5px solid #e5e5e5;
  overflow: hidden;
}

.conv-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  padding: 8px 12px;
  border-bottom: 0.5px solid #e5e5e5;
  background: #fafafa;
}

.conv-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 99px;
  }
}

.msg-in {
  display: flex;
  gap: 8px;
}

.msg-out {
  display: flex;
  gap: 8px;
  flex-direction: row-reverse;
}

.msg-av {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-end;
}

.topic-row {
  display: flex;
  gap: 4px;
  margin-bottom: 3px;
}

.topic-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 99px;
}

.bubble {
  padding: 8px 11px;
  font-size: 12px;
  color: #666;
  line-height: 1.6;
  max-width: 85%;
  border-radius: 12px;
}

.msg-in .bubble {
  background: #fafafa;
  border: 0.5px solid #e5e5e5;
  border-radius: 0 12px 12px 12px;
}

.msg-out .bubble {
  background: #E1F5EE;
  border: 0.5px solid #5DCAA5;
  border-radius: 12px 0 12px 12px;
}

.bubble-pending {
  background: #EEEDFE !important;
  border: 0.5px solid #AFA9EC !important;
}

.bubble-meta {
  font-size: 10px;
  color: #999;
  margin-top: 3px;
}

.msg-out .bubble-meta {
  text-align: right;
}

// AI Column
.ai-col {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  padding: 8px 12px;
  border-bottom: 0.5px solid #e5e5e5;
  background: #fafafa;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ai-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 7px;
  border-radius: 99px;
  background: #EEEDFE;
  color: #3C3489;
  border: 0.5px solid #AFA9EC;
}

.conf-badge {
  margin-left: auto;
  font-size: 10px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 4px;
}

.cdot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.ai-inner {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 99px;
  }
}

.ai-draft-box {
  background: #fafafa;
  border: 0.5px solid #e5e5e5;
  border-radius: 12px;
  overflow: hidden;
}

.adb-head {
  padding: 7px 10px;
  border-bottom: 0.5px solid #e5e5e5;
  display: flex;
  align-items: center;
  gap: 6px;
}

.edit-field {
  padding: 8px 10px;
  font-size: 12px;
  color: #333;
  line-height: 1.65;
  background: #fff;
  border: none;
  outline: none;
  width: 100%;
  min-height: 70px;
  resize: none;
  font-family: inherit;
}

.adb-foot {
  padding: 6px 10px;
  border-top: 0.5px solid #e5e5e5;
  display: flex;
  gap: 5px;
  justify-content: flex-end;
}

.note-section {
  border-top: 0.5px solid #e5e5e5;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.note-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
}

.note-area {
  border: 0.5px solid #e5e5e5;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 11px;
  color: #666;
  background: #fafafa;
  resize: none;
  width: 100%;
  height: 44px;
  outline: none;
  font-family: inherit;
}

.action-row {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.processed-box {
  text-align: center;
  padding: 20px;
}

.processed-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;

  &.success {
    background: #E1F5EE;
    color: #1D9E75;
  }

  &.danger {
    background: #FCEBEB;
    color: #E24B4A;
  }
}

.sent-box {
  background: #fafafa;
  border: 0.5px solid #e5e5e5;
  border-radius: 6px;
  padding: 10px 12px;
}

.sent-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  margin-bottom: 6px;
}

.sent-content {
  font-size: 12px;
  color: #333;
  line-height: 1.6;
  white-space: pre-wrap;
}

.resend-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 0.5px solid #e5e5e5;

  .edit-field {
    background: #fff;
    border: 0.5px solid #e5e5e5;
    border-radius: 8px;
    margin-top: 8px;
  }
}

.approval-stats {
  background: #fafafa;
  border: 0.5px solid #e5e5e5;
  border-radius: 6px;
  padding: 10px 12px;
}

.stats-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  margin-bottom: 8px;
}

.stats-bars {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.stat-bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.stat-bar-label {
  color: #666;
  width: 90px;
}

.stat-bar-track {
  flex: 1;
  height: 6px;
  background: #fff;
  border-radius: 99px;
}

.stat-bar-fill {
  height: 6px;
  border-radius: 99px;
}

.stat-bar-value {
  font-weight: 500;
  width: 28px;
  text-align: right;
}

// Group Config Modal
.group-config-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.group-config-desc {
  font-size: 13px;
  color: #666;
  margin: 0;
  padding: 10px 12px;
  background: #f8f8f8;
  border-radius: 6px;
  border-left: 3px solid #1D9E75;
}

.group-search-box {
  position: relative;
}

.group-search-input {
  width: 100%;
  padding: 14px 18px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #1D9E75;
    box-shadow: 0 0 0 2px rgba(29, 158, 117, 0.1);
  }

  &::placeholder {
    color: #bbb;
  }
}

.group-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e5e5;
  border-radius: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 99px;
  }
}

.group-checkbox-bs {
  flex-shrink: 0;
  margin-right: 4px;
}

.group-target-btn {
  flex-shrink: 0;
  margin-left: auto;
  padding: 3px 10px;
  border: 1px solid #D3E5DC;
  border-radius: 99px;
  background: #fff;
  color: #0F6E56;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;

  &:hover { border-color: #1D9E75; }
}

.ai-target-hint {
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: #555;

  &__group { font-weight: 600; color: #222; margin-bottom: 6px; word-break: break-word; }

  &__note {
    padding: 6px 10px;
    border-left: 3px solid #1D9E75;
    border-radius: 4px;
    background: #E1F5EE;
    color: #0F6E56;
    font-weight: 500;
  }
}
.ai-target-empty { font-size: 12px; color: #999; font-style: italic; }
.ai-target-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  padding: 8px 10px;
  border: 1px solid #E3EFE9;
  border-radius: 8px;
  cursor: pointer;

  &.active { border-color: #1D9E75; background: #E1F5EE; }
  input { margin: 0; flex-shrink: 0; }
}
.ai-target-avatar { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.ai-target-text { min-width: 0; }
.ai-target-name { font-size: 13px; font-weight: 500; color: #222; }
.ai-target-meta { font-size: 11px; color: #888; word-break: break-word; }

.group-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }

  &.selected {
    background: #E1F5EE;
  }
}

.group-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;

  .group-item.selected & {
    background: #1D9E75;
    border-color: #1D9E75;
  }
}

.check-icon {
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.group-info {
  flex: 1;
  min-width: 0;
}

.group-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.group-id {
  font-size: 11px;
  color: #999;
}

// Nút mở modal văn phong trong modal Cấu hình nhóm
.group-search-box {
  display: flex;
  align-items: center;
  gap: 8px;

  .group-search-input { flex: 1; min-width: 0; }

  // Cao bằng ô tìm, bo góc giống ô tìm cho cùng 1 hàng
  .group-style-btn {
    align-self: stretch;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 13px;
  }
}

.group-style-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid #D3E5DC;
  border-radius: 99px;
  background: #fff;
  color: #555;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;

  &:hover { border-color: #1D9E75; color: #0F6E56; }

  &:disabled { opacity: 0.5; cursor: default; }
}

// Modal văn phong: mỗi chủ đề 1 hàng thẻ chọn coach
.style-hint {
  margin: 0 0 12px;
  font-size: 12px;
  color: #777;
}

.style-topic {
  padding: 10px 0;
  border-top: 1px solid #EEF2F0;

  &__title {
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #222;
  }
}

.style-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.style-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 160px;
  padding: 8px 12px;
  border: 1px solid #E3EFE9;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) { border-color: #1D9E75; }

  &.active {
    border-color: #1D9E75;
    background: #E1F5EE;
    box-shadow: inset 0 0 0 1px #1D9E75;
  }

  &:disabled { opacity: 0.6; cursor: default; }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: #222;
  }

  &__meta {
    font-size: 11px;
    color: #888;
  }
}

.style-empty {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.style-footer__hint {
  margin-right: auto;
  font-size: 12px;
  color: #BA7517;
  white-space: nowrap;
}

// Danh sách câu mẫu của coach đã chọn - tick tối đa 5
.style-samples {
  margin-top: 8px;
  border: 1px solid #E3EFE9;
  border-radius: 8px;
  background: #FAFCFB;

  &__toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border: 0;
    background: transparent;
    font-size: 12px;
    color: #555;
    text-align: left;
    cursor: pointer;

    strong { color: #0F6E56; font-weight: 600; }
  }

  &__caret { color: #1D9E75; white-space: nowrap; }

  &__body {
    max-height: 520px;
    overflow-y: auto;
    padding: 0 12px 10px;
  }

  &__bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 4px 0 8px;
    font-size: 11px;
    color: #888;
  }

  &__clear {
    border: 0;
    background: transparent;
    color: #C0392B;
    font-size: 11px;
    cursor: pointer;

    &:disabled { opacity: 0.5; cursor: default; }
  }
}

.style-sample {
  display: flex;
  gap: 10px;
  margin: 0 0 6px;
  padding: 8px 10px;
  border: 1px solid #EEF2F0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;

  input { margin-top: 3px; flex-shrink: 0; }

  &.active { border-color: #1D9E75; background: #F0FAF6; }
  &.disabled { opacity: 0.55; cursor: not-allowed; }

  &__text { min-width: 0; font-size: 12px; line-height: 1.45; color: #333; }
  &__q, &__a {
    white-space: pre-line;
    word-break: break-word;
  }
  &__q { color: #666; }
  &__a { margin-top: 2px; }
  &__meta { margin-top: 3px; font-size: 11px; color: #999; }
}

@media (max-width: 767px) {
  .style-option { flex: 1 1 100%; min-width: 0; min-height: 44px; }
  // Để cả modal cuộn 1 lần, tránh kẹt cuộn lồng nhau trên điện thoại
  .style-samples__body { max-height: none; overflow: visible; }
  .style-samples__toggle { min-height: 44px; }
  .style-sample input { width: 18px; height: 18px; }
}

.group-style-picker {
  display: flex;
  align-items: center;
  gap: 6px;
  // Không co lại khi tên nhóm dài, nhưng cũng không đẩy vỡ hàng
  flex: 0 0 auto;
  cursor: default;
}

.style-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
}

.style-select {
  max-width: 200px;
  padding: 3px 6px;
  font-size: 11px;
  color: #333;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #BA7517;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.no-groups {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.selected-count {
  font-size: 13px;
  color: #666;
  text-align: right;
  padding: 8px 0;

  strong {
    color: #1D9E75;
  }
}

.d-chat-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #1D9E75;
  border: none;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

}

// Back button (mobile only)
.d-back-btn {
  display: none;
  background: none;
  border: none;
  padding: 8px;
  margin-right: 4px;
  cursor: pointer;
  color: #555;
  border-radius: 6px;
  min-width: 36px;
  min-height: 36px;
  align-items: center;
  justify-content: center;
  &:hover { background: #f5f5f5; }
}

// ── Mobile responsive ──────────────────────────────────────────
@media (max-width: 767px) {
  .coach-cms {
    height: calc(100vh - 50px);
    overflow: hidden;
    width: 100%;
  }

  // On mobile: stack sidebar + list + detail vertically,
  // show only the active panel at once via JS class on .shell
  .shell {
    display: flex !important;
    flex-direction: column;
    border-radius: 0;
    width: 100%;
    align-items: stretch;

    > * {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }
  }

  .tbar-right .btn {
    padding: 4px 8px;
    font-size: 11px;
    line-height: 1.4;
  }

  .tbar-right .btn .svg-icon {
    width: 14px;
    height: 14px;
  }

  // Topbar stays at top
  .topbar {
    padding: 0 12px;
    min-height: 44px;
    flex-shrink: 0;
  }

  // Sidebar becomes a collapsible filter bar at top
  .sidebar {
    border-right: none;
    border-bottom: 0.5px solid #e5e5e5;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0;
    padding: 6px 8px;
    overflow: hidden;
    flex-shrink: 0;
    max-height: 46px;
    transition: max-height 0.25s ease;

    &.mobile-expanded {
      max-height: 300px;
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    }
  }

  .sidebar-mobile-toggle {
    display: flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: none;
    font-size: 12px;
    font-weight: 500;
    color: #555;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 6px;
    white-space: nowrap;
    flex-shrink: 0;
    &:active { background: #f0f0f0; }
  }

  .sec-lbl {
    display: none;
  }

  .nav {
    padding: 5px 10px;
    font-size: 12px;
    border-radius: 99px;
    border: 0.5px solid #e5e5e5;
    white-space: nowrap;
    flex-shrink: 0;

    &.on {
      background: #E1F5EE;
      color: #0F6E56;
      border-color: #5DCAA5;
    }
  }

  .coach-filter-wrapper {
    width: 100%;
    padding: 4px 0;
    margin-bottom: 4px;
  }

  // Patient list takes full height when visible
  .kh-list {
    flex: 1;
    min-height: 0;
    border-right: none;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    overflow-x: hidden;
  }

  .kh-items {
    width: 100%;
  }

  .acc-group,
  .acc-group-header,
  .acc-kpis,
  .acc-kpi,
  .acc-dates,
  .acc-date {
    width: 100%;
    box-sizing: border-box;
  }

  // Detail panel: fullscreen overlay on mobile
  .detail {
    position: fixed;
    inset: 0;
    top: 50px;
    z-index: 100;
    background: #fff;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 24px;
    transform: translateX(100%);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);

    &.mobile-visible {
      transform: translateX(0);
    }
  }

  .d-body {
    overflow: visible;
  }

  .edit-textarea {
    min-height: 120px;
    resize: none;
    transition: max-height 0.25s ease;
  }


  .d-back-btn {
    display: flex;
  }

  .d-chat-btn {
    display: flex;
  }

  // Back button inside detail header
  .d-header {
    background: #fff;
    z-index: 1;
    flex-shrink: 0;
  }

  .stats-collapse-toggle,
  .compare-collapse-toggle {
    flex-shrink: 0;
  }

  .stats-collapsible {
    flex-shrink: 0;
  }

  .compare-section {
    max-height: 35vh;
    overflow-y: auto;
  }

  // Stats grid 2 columns on mobile
  .dashboard-stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  // Compare section stack vertically
  .compare-section {
    flex-direction: column;
  }

  .d-body {
    overflow: visible;
  }

  // Edit textarea wider on mobile
  .edit-textarea {
    min-height: 220px;
    font-size: 14px;
  }

  // Touch-friendly buttons
  .btn {
    min-height: 44px;
    font-size: 14px;
  }

  // Accordion items bigger tap targets
  .acc-group-header,
  .acc-kpi-header,
  .acc-date-header,
  .acc-msg {
    min-height: 44px;
  }

  // Hide desktop chat panel on mobile, show overlay instead
  .chp-wrap {
    display: none !important;
  }

}

// Floating chat button (all screens)
.mobile-chat-fab {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 40px;
  right: 32px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #1D9E75;
  color: #fff;
  border: none;
  box-shadow: 0 4px 20px rgba(29,158,117,0.45);
  cursor: pointer;
  z-index: 200;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover { transform: scale(1.07); box-shadow: 0 6px 24px rgba(29,158,117,0.55); }
  &:active { transform: scale(0.93); }
}

@media (max-width: 767px) {
  .mobile-chat-fab {
    top: 160px;
    bottom: unset;
    right: 16px;
    width: 52px;
    height: 52px;
  }

  .mobile-chat-fab__badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #e74c3c;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }

}

.mobile-chat-overlay {
  position: fixed;
  top: 50px;
  left: 0;
  bottom: 0;
  width: 85vw;
  max-width: 360px;
  background: #fff;
  z-index: 300;
  display: flex;
  flex-direction: column;
  border-radius: 0 16px 16px 0;
  box-shadow: 4px 0 24px rgba(0,0,0,0.15);
  overflow: hidden;
}

.mobile-chat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 0.5px solid #e5e5e5;
  flex-shrink: 0;
}

.mobile-chat-title {
  font-size: 14px;
  font-weight: 600;
  color: #1D9E75;
}

.mobile-chat-group {
  font-size: 12px;
  color: #888;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-chat-close {
  background: none;
  border: none;
  font-size: 16px;
  color: #aaa;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  &:active { background: #f0f0f0; }
}

.mobile-chat-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 12px;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
}

.chat-direct-input {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid #e5e5e5;
  background: #fff;
  flex-shrink: 0;
  /* chip file chiem tron mot hang phia tren o nhap */
  flex-wrap: wrap;
}

.chat-pending {
  flex: 0 0 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f2f4f3;
  font-size: 12px;
  color: #444;
}

.chat-pending__thumb {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
}

.chat-pending__name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-pending__remove {
  border: none;
  background: transparent;
  color: #888;
  font-size: 16px;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;

  &:hover { color: #d33; }
}

.chp-msg__uploading {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
}

.chp-msg__file {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #185FA5;
  text-decoration: none;
  padding: 6px 10px;
  background: #E6F1FB;
  border-radius: 6px;
  border: 1px solid #85B7EB;

  &:hover { background: #C8DFF5; }
}

.chat-attach-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #888;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s;

  &:hover { color: #1D9E75; background: #E1F5EE; }
  &.disabled { opacity: 0.4; cursor: not-allowed; }
}

.chat-direct-textarea {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #333;
  resize: none;
  outline: none;
  line-height: 1.4;
  transition: border-color 0.15s;

  &::placeholder { color: #bbb; }
  &:focus { border-color: #5DCAA5; }
}

.chat-direct-send {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #1D9E75;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover { background: #17876A; }
  &:disabled { background: #ccc; cursor: not-allowed; }
}

@media (min-width: 768px) {
  .chat-overlay {
    top: 0;
    width: 400px;
    max-width: 45vw;
    border-radius: 0 16px 16px 0;
    box-shadow: 4px 0 32px rgba(0,0,0,0.15);
  }
}

.mobile-textarea-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  row-gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid #e5e5e5;
  background: #fafafa;

  .btn {
    white-space: nowrap;
    padding: 6px 10px;
    font-size: 12px;
  }
}

.mobile-float-actions {
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebebeb;
  box-shadow: 0 4px 24px rgba(0,0,0,0.12);
  z-index: 500;

  .btn {
    height: 44px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
  }

  .btn-plain {
    flex-shrink: 0;
    padding: 0 16px;
    background: #f5f5f5;
    border: none;
    color: #555;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .btn-success {
    flex: 1;
    justify-content: center;
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

@media (min-width: 768px) {
  .mobile-float-actions {
    display: none !important;
  }
}

.float-actions-enter-active,
.float-actions-leave-active { transition: transform 0.2s ease; }
.float-actions-enter,
.float-actions-leave-to { transform: translateY(100%); }

.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.chat-slide-enter,
.chat-slide-leave-to {
  transform: translateX(-100%);
}

.sidebar-mobile-toggle {
  display: none;
}

.mobile-toolbar {
  display: none;
}

@media (max-width: 767px) {
  .mobile-toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-bottom: 0.5px solid #e5e5e5;
    background: #fff;
    flex-shrink: 0;
    position: relative;
    z-index: 50;
  }

  .mtb-spacer {
    flex: 1;
  }

  .mtb-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    border: 0.5px solid #e0e0e0;
    border-radius: 20px;
    background: #fff;
    font-size: 12px;
    color: #555;
    cursor: pointer;
    position: relative;
    transition: all 0.15s;

    &.active {
      background: #f0f9f5;
      border-color: #1D9E75;
      color: #0F6E56;
    }

    &.mtb-btn--icon {
      padding: 6px 10px;
      border-radius: 50%;
      width: 34px;
      height: 34px;
      justify-content: center;
    }
  }

  .mtb-dot {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #1D9E75;
  }

  .mtb-panel {
    position: fixed;
    top: 96px;
    left: 0;
    right: 0;
    background: #fff;
    border-bottom: 0.5px solid #e5e5e5;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    padding: 12px;
    z-index: 150;
    max-height: 60vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .mtb-search-panel {
    position: fixed;
    top: 96px;
    left: 0;
    right: 0;
    background: #fff;
    border-bottom: 0.5px solid #e5e5e5;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    padding: 10px 12px;
    z-index: 150;
  }

  .mtb-section {
    margin-bottom: 12px;
    &:last-child { margin-bottom: 0; }
  }

  .mtb-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.8px;
    color: #aaa;
    margin-bottom: 8px;
  }

  .mtb-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .search-mini--full {
    width: 100%;
    border: 0.5px solid #e0e0e0;
    border-radius: 8px;
    padding: 8px 10px;
  }

  .mtb-clear {
    background: none;
    border: none;
    color: #aaa;
    cursor: pointer;
    font-size: 13px;
    padding: 0 2px;
  }

  .mtb-drop-enter-active,
  .mtb-drop-leave-active { transition: opacity 0.15s, transform 0.15s; }
  .mtb-drop-enter, .mtb-drop-leave-to { opacity: 0; transform: translateY(-6px); }
}

.chp-toggle-btn {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border: none;
  border-radius: 20px;
  background: #f0f9f5;
  color: #0F6E56;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 1px 4px rgba(29,158,117,0.15);

  &:hover { background: #d9f2ea; box-shadow: 0 2px 8px rgba(29,158,117,0.25); }
  &.active { background: #1D9E75; color: #fff; box-shadow: 0 2px 8px rgba(29,158,117,0.35); }
}

// Chat History Panel (inline in detail)
.chp-wrap {
  border: 0.5px solid #e5e5e5;
  border-radius: 8px;
  margin: 0 16px 12px;
  display: flex;
  flex-direction: column;
  height: 360px;
  min-height: 180px;
  background: #fafafa;
  overflow: hidden;
}

.chp-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 0.5px solid #e5e5e5;
  background: #fff;
  min-height: 46px;
  flex-shrink: 0;
}

.chp-title {
  font-size: 13px;
  font-weight: 600;
  color: #1D9E75;
  white-space: nowrap;
}

.chp-group-name {
  font-size: 11px;
  color: #888;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chp-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  font-size: 14px;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1;

  &:hover { color: #333; background: #f0f0f0; }
}

.chp-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
}

.chp-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #888;
  font-size: 13px;
  margin: auto;
}

.chp-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #e0e0e0;
  border-top-color: #1D9E75;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.chp-empty {
  margin: auto;
  color: #aaa;
  font-size: 13px;
}

.chp-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chp-msg {
  display: flex;
  gap: 6px;
  align-items: flex-end;

  &--oa {
    flex-direction: row-reverse;

    .chp-msg__bubble {
      background: #1D9E75;
      color: #fff;
      border-radius: 12px 12px 2px 12px;
    }

    .chp-msg__time { color: rgba(255,255,255,0.7); }
  }

  &--user .chp-msg__bubble {
    background: #fff;
    border: 0.5px solid #e5e5e5;
    border-radius: 12px 12px 12px 2px;
  }
}

.chp-msg__avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e0e0e0;
  color: #555;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chp-msg__bubble {
  max-width: 85%;
  padding: 7px 10px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}

.chp-msg__name {
  font-size: 10px;
  font-weight: 600;
  color: #1D9E75;
  margin-bottom: 2px;
}

.chp-msg__text {
  font-size: 12px;
  line-height: 1.5;
  word-break: break-word;
}

/* Chi tach dong khi chu di kem hinh/file */
.chp-msg__text--caption {
  margin-top: 4px;
}

.chp-msg__img {
  max-width: 160px;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.85;
  }
}

.chp-msg__video {
  max-width: 160px;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.85;
  }
}

.chp-msg__file {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 6px;
  color: #333;
  text-decoration: none;
  font-size: 12px;
  max-width: 200px;
  transition: background 0.2s;
  &:hover {
    background: #eee;
    text-decoration: none;
    color: #333;
  }
}

.chp-msg__file-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.chp-msg__file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chp-msg__time {
  font-size: 10px;
  color: #bbb;
  margin-top: 3px;
  text-align: right;
}

// Share modal
.share-modal-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 60vh;
  overflow-y: auto;
}

.share-modal-desc {
  font-size: 13px;
  color: #666;
  margin: 0;
}

.share-loading {
  font-size: 13px;
  color: #999;
  padding: 12px 0;
}

.share-coach-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 4px;
}

.share-coach-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s;

  &:hover { background: #f5f5f5; }

  &.selected {
    background: #EEF2FF;

    .share-coach-checkbox {
      background: #4338CA;
      border-color: #4338CA;
      color: #fff;
    }
  }
}

.share-coach-checkbox {
  width: 18px;
  height: 18px;
  border: 1.5px solid #ccc;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
  color: transparent;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}

.share-coach-name {
  font-size: 13px;
  font-weight: 500;
  color: #222;
}

.no-coaches {
  padding: 16px;
  text-align: center;
  color: #aaa;
  font-size: 13px;
}

.selected-count {
  font-size: 12px;
  color: #888;
}

.share-original-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.share-original-content {
  background: #f8f8f8;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: #555;
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 120px;
  overflow-y: auto;
}

.share-note-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.share-note-label {
  font-size: 12px;
  font-weight: 500;
  color: #555;
  margin: 0;
}

.share-note-textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: #333;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: #bbb; }
  &:focus { border-color: #A5B4FC; }
}

.btn-share {
  background: #EEF2FF;
  color: #4338CA;
  border-color: #A5B4FC;

  &:hover {
    background: #C7D2FE;
    color: #312E81;
  }
}

// Media Preview Modal
.media-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.media-preview-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-preview-close {
  position: absolute;
  top: -40px;
  right: -10px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.media-preview-img {
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.media-preview-video {
  max-width: 90vw;
  max-height: 85vh;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
</style>

<style lang="scss">
// b-modal tự render .modal-dialog, scoped style không chạm tới được
.modal-dialog.kov-dialog {
  width: 95vw;
  max-width: 1280px;
}

// Mobile: toàn màn hình - modal nổi giữa màn hình nhỏ thì mất chỗ vô ích
@media (max-width: 767px) {
  .modal-dialog.kov-dialog {
    width: 100vw;
    max-width: 100vw;
    height: 100%;
    min-height: 100%;
    margin: 0;

    .modal-content {
      height: 100%;
      max-height: 100%;
      border: none;
      border-radius: 0;
    }
  }
}
</style>
